import { ResgateTabletop } from './ResgateTabletop'
import { Edges, Html, TransformControls } from '@react-three/drei'
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react'
import * as THREE from 'three'
import type { Group } from 'three'
import {
  ancoraMaisProxima,
  alvoAmassadoNoPonto,
  ancorasLivresDoVeiculo,
  avaliarEstabilizacaoDaCena,
  colocacaoLocalDaVitimaNoVeiculo,
  encaixarPosicaoXZ,
  estadoVisualDeformacoesTabletop,
  pontoMundoNoVeiculo,
  podeApoiarEmVeiculo,
  type AvaliacaoEstabilizacaoPorVeiculo,
  type EstadoColocacao,
  type ObjetoTabletop,
  type PrioridadeTriagem,
  type PosturaVitimaSolo,
  type TipoObjetoTabletop,
  type TransformacaoTabletop,
} from '../../dominio/tabletop'
import { buscarIntersecaoSuperficie, calcularRotacaoSuperficie } from './superficieApoio'
import { AlcasCorda3D } from './AlcasCorda3D'
import { estadoNavegacao } from '../camera/estadoNavegacao'
import { FATOR_AJUSTE_FINO, ponteiroArrastou, TOLERANCIA_ARRASTO_PECA_MOUSE_PX, type PontoDeTela } from '../gestoPonteiro'
import type { AparenciaRisco } from '../../dominio/tabletop/riscosDaCena'
import type { AncoragemTabletop, CordaTabletop, FuncaoCorda, MaterialAbsorvente, ReferenciaPonto, TipoAncoragem } from '../../dominio/tabletop'
import {
  atualizarSuspensao,
  ESTADO_SUSPENSAO_INICIAL,
  type EstadoSuspensao,
  type ForcaPerturbacao,
} from '../../dominio/didatico/suspensaoVeiculo'
import { HATCH_COMPACTO, SEDAN_MEDIO, perfilVeiculoPorId, type PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import type { MapaEstados } from '../../dominio/didatico/estadoPecas'
import type { EstadoManejoVidro, IdVidro } from '../../dominio/didatico/manejoVidros'
import { ADULTO_MASCULINO, perfilVitimaPorId } from '../../dominio/didatico/perfisVitima'
import { POSE_ALINHADA, POSE_SENTADA, POSE_SENTADA_NO_CHAO } from '../../dominio/didatico/rigVitima'
import { VeiculoAlvoCompleto, VeiculoDidatico } from '../veiculo/VeiculoDidatico'
import type { EstadoDeformacaoVeiculo } from '../../dominio/didatico/campoDeformacao'
import { VitimaProcedural } from '../vitima/VitimaProcedural'
import { DetritosVidroPersistentes3D } from '../vidros/DetritosVidroPersistentes3D'
import { StepChock3D, CunhaResgate3D, BlocoCribbing3D } from '../estabilizacao/EquipamentosCribbing'
import { VStrut3D } from '../estabilizacao/VStrut3D'
import { PistaAsfalto3D } from './PistaAsfalto3D'
import { PontosAncoragem3D } from './PontosAncoragem3D'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { AncoragemBasica3D } from './AncoragemBasica3D'
import { EnvolvimentoTabletop, PlacaAncoragemTabletop } from './AncoragensEspeciais3D'
import { Cordas3D } from './Cordas3D'
import { ContextoEquipamentos, FATOR_DESTAQUE, TIPOS_EM_DESTAQUE, useEquipamentosNaCena, type EquipamentosNaCena } from './contextoEquipamentos'
import { VELOCIDADE_PUXADA_M_S } from './Cordas3D'
import { giroDasRoldanasNaCorda, RAIO_RODANA_POLIA_M, orientacaoDoMosquetao, ehPecaDeEncaixe, pontoDeEncaixeMaisPerto, type CenaTabletop as CenaTabletopDominio, type VetorTabletop } from '../../dominio/tabletop'
import { mundoParaLocalDoObjeto } from '../../dominio/tabletop/ancoragem'
import { Solo3D, SoloMato3D } from './Solo3D'
import { QuedaAnimada3D, TorreTabletop, TrabalhadorTabletop, ZonaLivreQueda3D } from './ObjetosAlturaTabletop'
import type { TalabarteAnalisado } from '../../dominio/tabletop/quedaEmAltura'
import type { QuadroQueda, ResultadoQuedaAnimada } from '../../dominio/tabletop/quedaAnimada'
import { Ancoragens3D } from './Ancoragens3D'
import { Simulacao3D, type QuadroParaCena } from './Simulacao3D'
import {
  BloqueadorTabletop,
  EscadaTabletop,
  NoOitoTabletop,
  NoFielTabletop,
  PoliaDuplaTabletop,
  PonteTabletop,
  SistemaVMTabletop,
  MacaTabletop,
  MosquetaoTabletop,
  PontoAncoragemTabletop,
  AlcaFitaTabletop,
  DescensorTabletop,
  ProtetorCordaTabletop,
  PoliaTabletop,
  TocoTabletop,
} from './ObjetosCordasTabletop'
import { CavaloTabletop, VacaTabletop } from './AnimaisTabletop'
import { EstacaTabletop } from './EstacaTabletop'
import { TerrenoTabletop } from './TerrenoTabletop'
import { alturaDosObjetosEm, buracosDoTerreno, ehTerreno } from '../../dominio/tabletop/terreno'
import { parametrosDoObjeto } from '../../dominio/tabletop/ancoragem'
import { composicaoDoSistema } from '../../dominio/tabletop/vantagemMecanica'
import { tipoDeFusivel } from '../../dominio/tabletop/fusivelSistema'
import { CaminhaoBombeirosTabletop } from './CaminhaoBombeiros3D'
import {
  BombeiroTabletop,
  BombeiroMedicoTabletop,
  AreaUsaTabletop,
  ManchaSangueTabletop,
  ExtintorPqsTabletop,
  MochilaTraumaTabletop,
  LonaFerramentasTabletop,
  PosteTabletop,
} from './ObjetosTaticosTabletop'
import { FaiscasChoqueEletrico3D, Fogo3D } from '../componentes/FogoEFaiscasChoque3D'
import { ProvedorMaquete, type ValorMaquete } from '../componentes/contextoMaquete'

/** Um toque em terreno só vale como "pisar" quando a face tocada está mais para chão do que para parede. */
function faceVoltadaParaCima(intersecao: THREE.Intersection): boolean {
  if (!intersecao.face) return true
  const normal = intersecao.face.normal.clone().transformDirection(intersecao.object.matrixWorld)
  return normal.y > 0.3
}

function resolverRegiaoHemorragiaVitima(objeto: ObjetoTabletop) {
  return objeto.vitima?.regiaoHemorragia
}

import { regioesVidroDoPerfil } from '../vidros/regioesVidro'
import { PREFIXO_SELECIONAVEL } from '../camera/comandosCamera'
import { CaixaArredondada } from '../componentes/CaixaArredondada'

export type FerramentaTabletop = 'translate' | 'rotate' | 'scale'

/** Cor do anel de triagem no chão, para a vítima selecionada ou não — mesma paleta do painel. */
const CORES_TRIAGEM_3D: Record<string, string> = {
  vermelho: '#ef4444',
  amarelo: '#f59e0b',
  verde: '#22c55e',
  preto: '#1f2937',
}

/** Mesma paleta de estado que a aba de Estabilização usa para um único veículo. */
const CORES_ESTADO_EQUIPAMENTO: Record<EstadoColocacao, string> = {
  correta: '#10b981',
  proxima: '#f59e0b',
  fora: '#ef4444',
  naoPrevista: '#ef4444',
}

/**
 * Camadas do carro selecionado no Table top: gaiola, zonas de absorção, SRS
 * e cinto visíveis, carroceria translúcida — o mesmo espírito do preset
 * "Estrutura" da aba de estrutura veicular, só que aplicado por carro.
 */
const CAMADAS_ESTRUTURA_TABLETOP: ValorMaquete['camadas'] = {
  estrutura: true,
  zonaDeformacao: true,
  srs: true,
  retencao: true,
  carroceria: true,
  habitaculo: true,
  // o Table top pode ter vários veículos na mesma cena e a aula ali é sobre
  // posição e estabilização, não sobre mecânica
  mecanica: false,
  energia: false,
  vitima: true,
}
const OPACIDADE_CARROCERIA_TABLETOP = 0.16
const NOOP_SELECIONAR = () => {}
const pararPropagacao = (evento: ThreeEvent<MouseEvent>) => evento.stopPropagation()
/**
 * Há uma peça esperando o toque no chão. Nesse caso o clique em cima de um
 * objeto (ex.: o calço vai sob a soleira, que na tela fica "em cima" do carro)
 * precisa atravessar até o plano do chão — senão a peça nunca nasce ali.
 */
const ContextoPosicionando = createContext(false)
/** A ferramenta de corda está ativa: os objetos deixam o toque passar para os pontos de ancoragem. */
const ContextoDesenhoCorda = createContext(false)
const LISTA_VAZIA: readonly string[] = []

export interface AtendimentoCena3D {
  readonly modoExibicao: 'aluno' | 'instrutor'
  readonly triagensDeclaradas: Readonly<Record<string, PrioridadeTriagem>>
  readonly colaresColocados: Readonly<Record<string, boolean>>
  /** sangramento visível por vítima enquanto o problema da ficha não é controlado */
  readonly sangramentos?: Readonly<Record<string, 'ativo' | 'parcial'>>
  /** aparência dos riscos na sessão: varrido, coberto, absorvente, área isolada, energizado */
  readonly riscos?: Readonly<Record<string, AparenciaRisco>>
  readonly idVitimaFoco?: string | null
  readonly regiaoDestaque?: string | null
}

/**
 * Pedido explícito de "testar estabilidade" num veículo. Cada pedido novo
 * leva um `serial` diferente, para o mesmo carro poder ser testado de novo.
 */
export interface PedidoTesteBalanco {
  readonly idVeiculo: string
  readonly serial: number
}

/** Empurrão aplicado pelo teste de estabilidade: para baixo, rolando e arfando ao mesmo tempo. */
/** Desnível, num único trecho do arrasto, a partir do qual soltar o trabalhador é soltá-lo no ar (m). */
const DEGRAU_DE_QUEDA_M = 0.5
const PASSO_AMOSTRA_DEGRAU_M = 0.1

/**
 * O terreno despenca (parede, barranco, borda de laje) entre dois pontos do arrasto? Distingue de uma rampa.
 * Devolve a primeira amostra já além da borda, ou `null` se não há degrau.
 */
function degrauNoTrecho(
  objetos: readonly ObjetoTabletop[],
  idIgnorado: string,
  x0: number, z0: number, x1: number, z1: number,
): readonly [number, number] | null {
  const n = Math.max(1, Math.ceil(Math.hypot(x1 - x0, z1 - z0) / PASSO_AMOSTRA_DEGRAU_M))
  let anterior = alturaDosObjetosEm(objetos, x0, z0, idIgnorado)
  for (let i = 1; i <= n; i += 1) {
    const x = x0 + ((x1 - x0) * i) / n
    const z = z0 + ((z1 - z0) * i) / n
    const h = alturaDosObjetosEm(objetos, x, z, idIgnorado)
    // uma rampa solta pouco por amostra; a parede, quase tudo de uma vez
    if (anterior - h > DEGRAU_DE_QUEDA_M * 0.8) return [x, z]
    anterior = h
  }
  return null
}

/** As peças da bandeja que se prendem num ponto de ancoragem ao tocar nele, em vez de nascer no chão. */
/** Peças pequenas, que ganham uma área de toque maior que o desenho. */
const PECA_PEQUENA: ReadonlySet<TipoObjetoTabletop> = new Set([
  'mosquetao', 'polia', 'poliaDupla', 'bloqueador', 'descensor', 'noOito', 'noFiel', 'pontoAncoragem',
  'placaAncoragem', 'protetorCorda', 'calco', 'cunha', 'estaca', 'sistemaVM', 'envolvimento',
])
const RAIO_AREA_DE_TOQUE_M = 0.28

const PECAS_QUE_VAO_NO_PONTO: ReadonlySet<TipoObjetoTabletop> = new Set(['mosquetao', 'polia', 'noFiel', 'noOito', 'pontoAncoragem', 'poliaDupla', 'bloqueador', 'descensor', 'placaAncoragem', 'sistemaVM'])

const FORCA_TESTE_BALANCO: ForcaPerturbacao = { forcaY: -14, torqueRoll: 10, torquePitch: 8, forcaZ: 3 }

export interface PropsCenaTabletop {
  objetos: readonly ObjetoTabletop[]
  idSelecionado: string | null
  ferramenta: FerramentaTabletop
  aoSelecionar: (id: string | null) => void
  aoTransformar: (id: string, transformacao: TransformacaoTabletop) => void
  aoAlterarManipulacao?: (ativa: boolean) => void
  aoCancelarManipulacao?: () => void
  /**
   * O trabalhador foi arrastado para fora de uma borda (queda de degrau) e solto: `posicao` é onde
   * ele ficou no ar, na altura do apoio que perdeu, para a cena fazê-lo cair dali.
   */
  aoSoltarTrabalhadorNoAr?: (id: string, posicao: VetorTabletop) => void
  encaixeAtivo?: boolean
  aoSelecionarPeca?: (idVeiculo: string, idPeca: string | null) => void
  atendimento?: AtendimentoCena3D
  /** Modo Atender: a cena fica travada (sem gizmo), só a seleção continua ativa para focar uma vítima. */
  somenteLeitura?: boolean
  /** Peça da bandeja aguardando um toque no chão para nascer ali (ver B2, `useTabletop.iniciarPosicionamento`). */
  tipoParaPosicionar?: TipoObjetoTabletop | null
  escolhendoAlvo?: boolean
  aoPosicionarNovaPeca?: (
    tipo: TipoObjetoTabletop,
    x: number,
    z: number,
    y?: number,
    rotacao?: readonly [number, number, number],
  ) => void
  /** B4: soltar uma vítima do chão perto de uma âncora livre já a senta ali, em vez de só reposicionar no chão. */
  aoVincularVitima?: (idVitima: string, idVeiculo: string, idAncora: string) => void
  /** B4 (volta pro chão): desvincula uma vítima já sentada, colocando-a exatamente na posição de mundo em que estava. */
  aoDesvincularVitima?: (idVitima: string) => void
  /** Selecionar um veículo não o balança mais; o balanço só vem deste pedido explícito. */
  pedidoTesteBalanco?: PedidoTesteBalanco | null
  pincel?: {
    ativo: boolean
    larguraM: number
    intrusaoM: number
    aoAplicar: (idVeiculo: string, ponto: Readonly<{ x: number; y: number; z: number }>) => void
  }
  /** Exibe a pista de asfalto com faixas viárias, acostamento e marcas de frenagem (padrão true). */
  exibirAsfalto?: boolean
  /** O chão da cena: a pista de asfalto (padrão) ou mato (rua no topo do barranco). */
  chao?: 'asfalto' | 'mato'
  /** Trava veículos e obstáculos no cenário, permitindo movimentar apenas equipamentos/ferramentas. */
  cenarioTravado?: boolean
  /** Mostra os pontos de ancoragem de todos os objetos; o objeto selecionado sempre mostra os dele. */
  mostrarPontosAncoragem?: boolean
  /** Setas de tração nas cordas e nas pernas das ancoragens (padrão ligado). */
  mostrarSetasTracao?: boolean
  /** Desenha os equipamentos pequenos maiores (só para a aula); os pontos acompanham. */
  destaqueEquipamentos?: boolean
  /** Anima a puxada: as cordas com sentido correm e as roldanas por onde passam giram. */
  animarPuxada?: boolean
  /** Talabartes analisados (queda em altura): desenha a zona livre de queda de cada um. */
  talabartes?: readonly TalabarteAnalisado[]
  /** Mostra a zona livre de queda (padrão ligado). */
  mostrarZonaLivreQueda?: boolean
  /** A queda animada do trabalhador, no quadro da régua de tempo. */
  quedaAnimada?: { resultado: ResultadoQuedaAnimada; indice: number; quadro: QuadroQueda } | null
  /** Cordas da cena; sem isto nenhuma é desenhada. */
  cordas?: readonly CordaTabletop[]
  idCordaSelecionada?: string | null
  /** Arrastar um ponto da corda selecionada (ponta ou meio) para outro ponto de ancoragem. */
  aoReposicionarPontoCorda?: (idCorda: string, indice: number, referencia: ReferenciaPonto) => void
  aoSelecionarCorda?: (id: string) => void
  /**
   * Corda em desenho: enquanto existir, os pontos de ancoragem de todos os
   * objetos aparecem e recebem toque, e os objetos deixam de ser selecionados.
   */
  desenhoCorda?: { funcao: FuncaoCorda; percurso: readonly ReferenciaPonto[] } | null
  aoEscolherPontoCorda?: (referencia: ReferenciaPonto) => void
  /**
   * Fora do desenho, tocar num ponto de ancoragem da peça selecionada abre as
   * ações do ponto (prender corda, fita, mosquetão, polia). Sem isto, os pontos
   * da peça selecionada são só marcadores.
   */
  aoTocarPontoAncoragem?: (referencia: ReferenciaPonto) => void
  /** Com uma peça da bandeja na mão, tocar num ponto de ancoragem a põe ali (o V da fita, o degrau, o olhal). */
  aoPosicionarNoPonto?: (referencia: ReferenciaPonto) => void
  /** O ponto tocado, com as ações abertas: fica destacado. */
  pontoEscolhido?: ReferenciaPonto | null
  /**
   * Uma ponta de corda esperando o novo ponto: como no desenho, os pontos de
   * todos os objetos acendem e o toque vai para `aoEscolherPontoCorda`.
   */
  escolhendoPontoDeCorda?: boolean
  /** Ancoragens em V, W ou M da cena; sem isto nenhuma é desenhada. */
  ancoragens?: readonly AncoragemTabletop[]
  idAncoragemSelecionada?: string | null
  aoSelecionarAncoragem?: (id: string) => void
  /** Ancoragem em desenho: os pontos de ancoragem acendem como no desenho de corda. */
  /** Quadro da simulação do V em exibição: a massa simulada e as pernas até ela. */
  simulacaoAncoragem?: QuadroParaCena | null
  desenhoAncoragem?: { tipo: TipoAncoragem; fixacoes: readonly ReferenciaPonto[]; centro: ReferenciaPonto | null } | null
  /**
   * Com `somenteLeitura` (Atender), ainda permite reposicionar por arraste o
   * que não é vítima — mover vítima na sessão é operação de atendimento.
   */
  arrastoNaSessao?: boolean
}

const TIPOS_EQUIPAMENTO_TABLETOP: readonly TipoObjetoTabletop[] = [
  'calco', 'cunha', 'vstrut', 'fitaCatraca', 'bloco', 'cobertor', 'lagrima', 'prancha',
  'escada', 'maca', 'polia', 'mosquetao', 'bloqueador', 'noOito', 'noFiel', 'poliaDupla', 'sistemaVM', 'torre', 'pontoAncoragem', 'placaAncoragem', 'envolvimento', 'descensor', 'protetorCorda',
]
const ehEquipamentoTabletop = (objeto: ObjetoTabletop) =>
  Boolean(objeto.equipamento || TIPOS_EQUIPAMENTO_TABLETOP.includes(objeto.tipo))

export function CenaTabletop({
  objetos,
  idSelecionado,
  ferramenta,
  aoSelecionar,
  aoTransformar,
  aoAlterarManipulacao,
  aoCancelarManipulacao,
  aoSoltarTrabalhadorNoAr,
  encaixeAtivo = false,
  aoSelecionarPeca,
  atendimento,
  somenteLeitura,
  tipoParaPosicionar,
  escolhendoAlvo = false,
  aoPosicionarNovaPeca,
  aoVincularVitima,
  aoDesvincularVitima,
  pedidoTesteBalanco,
  pincel,
  exibirAsfalto = true,
  chao = 'asfalto',
  cenarioTravado = false,
  mostrarPontosAncoragem = false,
  mostrarSetasTracao = true,
  destaqueEquipamentos = false,
  animarPuxada = false,
  talabartes,
  mostrarZonaLivreQueda = true,
  quedaAnimada,
  cordas,
  idCordaSelecionada = null,
  aoReposicionarPontoCorda,
  aoSelecionarCorda,
  desenhoCorda,
  aoEscolherPontoCorda,
  aoTocarPontoAncoragem,
  aoPosicionarNoPonto,
  pontoEscolhido,
  escolhendoPontoDeCorda,
  ancoragens,
  idAncoragemSelecionada = null,
  aoSelecionarAncoragem,
  desenhoAncoragem,
  simulacaoAncoragem,
  arrastoNaSessao = false,
}: PropsCenaTabletop) {

  // vítimas vinculadas a um veículo são renderizadas DENTRO do grupo do carro
  // (para herdar rolagem/guinada automaticamente) e saem da lista de objetos
  // independentes, que continua tendo gizmo próprio
  const vitimasPorVeiculo = useMemo(() => {
    const mapa = new Map<string, ObjetoTabletop[]>()
    for (const objeto of objetos) {
      const local = objeto.vitima?.local
      if (local?.tipo !== 'veiculo') continue
      const lista = mapa.get(local.idVeiculo) ?? []
      lista.push(objeto)
      mapa.set(local.idVeiculo, lista)
    }
    return mapa
  }, [objetos])

  const idsVinculados = useMemo(
    () => new Set(objetos.filter((o) => o.vitima?.local.tipo === 'veiculo').map((o) => o.id)),
    [objetos],
  )

  const { estadoPorEquipamento, avaliacoesPorVeiculo } = useMemo(() => {
    const estados = new Map<string, EstadoColocacao>()
    const porVeiculo = new Map<string, AvaliacaoEstabilizacaoPorVeiculo>()
    for (const avaliacao of avaliarEstabilizacaoDaCena(objetos)) {
      porVeiculo.set(avaliacao.idVeiculo, avaliacao)
      for (const [id, estado] of avaliacao.estadoPorEquipamento) estados.set(id, estado)
    }
    return { estadoPorEquipamento: estados, avaliacoesPorVeiculo: porVeiculo }
  }, [objetos])

  // A peça só se move depois de um arraste intencional. No touch, pressionar
  // por 350 ms ativa o gesto; mover antes disso continua sendo órbita da câmera.
  const [arrastandoObjetoId, setArrastandoObjetoId] = useState<string | null>(null)
  const [ancoraDestacada, setAncoraDestacada] = useState<{ idVeiculo: string; idAncora: string } | null>(null)
  const [pontoHoverPosicionar, setPontoHoverPosicionar] = useState<[number, number, number] | null>(null)
  const ancoraDestacadaRef = useRef<{ idVeiculo: string; idAncora: string } | null>(null)
  const gestoRef = useRef<{
    id: string
    pointerId: number
    inicio: PontoDeTela
    soloInicial: THREE.Vector3
    origem: TransformacaoTabletop['posicao'] | null
    temporizador: number | null
    ativo: boolean
    manipulando: boolean
    moveu: boolean
    desvinculou: boolean
    soltoNoAr: VetorTabletop | null
  } | null>(null)

  let camera: THREE.Camera | undefined
  let raycaster: THREE.Raycaster | undefined
  let canvas: HTMLCanvasElement | undefined
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const three = useThree()
    camera = three.camera
    raycaster = three.raycaster
    canvas = three.gl.domElement
  } catch {
    // fora do <Canvas> (ex.: testes de unidade)
  }
  const buracos = useMemo(() => objetos.flatMap(buracosDoTerreno), [objetos])
  const planoChao = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), [])
  const veiculos3DRef = useRef<Map<string, THREE.Group>>(new Map())
  const registrarVeiculo3D = useCallback((id: string, grp: THREE.Group | null) => {
    if (grp) veiculos3DRef.current.set(id, grp)
    else veiculos3DRef.current.delete(id)
  }, [])
  const pontoNoSolo = (x: number, y: number): THREE.Vector3 | null => {
    if (!canvas || !camera || !raycaster) return null
    const rect = canvas.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return null
    raycaster.setFromCamera(
      new THREE.Vector2(((x - rect.left) / rect.width) * 2 - 1, -((y - rect.top) / rect.height) * 2 + 1),
      camera,
    )
    return raycaster.ray.intersectPlane(planoChao, new THREE.Vector3())
  }

  const objetoArrastado = arrastandoObjetoId ? objetos.find((o) => o.id === arrastandoObjetoId) ?? null : null
  const vitimaArrastadaPodeSentar =
    Boolean(aoVincularVitima) && objetoArrastado?.tipo === 'vitima' && objetoArrastado.vitima?.local.tipo !== 'veiculo'

  const ancorasLivresNaCena = useMemo(() => {
    if (!vitimaArrastadaPodeSentar) return []
    const marcadores: Array<{ idVeiculo: string; idAncora: string; posicao: readonly [number, number, number] }> = []
    for (const veiculo of objetos) {
      if (!veiculo.veiculo) continue
      for (const ancora of ancorasLivresDoVeiculo(veiculo, objetos)) {
        const mundo = pontoMundoNoVeiculo(ancora.posicao, veiculo)
        marcadores.push({ idVeiculo: veiculo.id, idAncora: ancora.id, posicao: [mundo.x, mundo.y, mundo.z] })
      }
    }
    return marcadores
  }, [vitimaArrastadaPodeSentar, objetos])

  const iniciarCandidatoArrasto = (id: string, evento: PointerEvent) => {
    if (gestoRef.current || escolhendoAlvo || ferramenta !== 'translate') return
    const objeto = objetos.find((item) => item.id === id)
    if (!objeto || objeto.bloqueado || (cenarioTravado && !ehEquipamentoTabletop(objeto))) return
    if (somenteLeitura && !(arrastoNaSessao && objeto.tipo !== 'vitima')) return
    const soloInicial = pontoNoSolo(evento.clientX, evento.clientY)
    if (!soloInicial) return
    const toque = evento.pointerType === 'touch'
    const gesto = {
      id, pointerId: evento.pointerId,
      inicio: { x: evento.clientX, y: evento.clientY },
      soloInicial,
      origem: objeto.vitima?.local.tipo === 'veiculo' ? null : objeto.transformacao.posicao,
      temporizador: null as number | null,
      ativo: false, manipulando: !toque, moveu: false, desvinculou: false,
      // trabalhador que passou por um degrau: onde ficou no ar (altura do apoio perdido)
      soltoNoAr: null as VetorTabletop | null,
    }
    gestoRef.current = gesto
    if (toque) {
      gesto.temporizador = window.setTimeout(() => {
        if (gestoRef.current !== gesto) return
        gesto.ativo = true
        gesto.manipulando = true
        aoAlterarManipulacao?.(true)
        setArrastandoObjetoId(id)
      }, 350)
    } else {
      // Suspende a órbita desde o pressionamento no mouse; se for só clique,
      // finalizarManipulacao não registra nenhum passo no histórico.
      aoAlterarManipulacao?.(true)
    }
  }

  useEffect(() => {
    const limparGesto = (cancelado: boolean) => {
      const gesto = gestoRef.current
      if (!gesto) return
      if (gesto.temporizador !== null) window.clearTimeout(gesto.temporizador)
      gestoRef.current = null
      if (!cancelado && gesto.moveu && ancoraDestacadaRef.current) {
        const ancora = ancoraDestacadaRef.current
        aoVincularVitima?.(gesto.id, ancora.idVeiculo, ancora.idAncora)
      }
      ancoraDestacadaRef.current = null
      setAncoraDestacada(null)
      setArrastandoObjetoId(null)
      if (gesto.manipulando) {
        if (cancelado && aoCancelarManipulacao) aoCancelarManipulacao()
        else aoAlterarManipulacao?.(false)
      }
      if (!cancelado && gesto.soltoNoAr) aoSoltarTrabalhadorNoAr?.(gesto.id, gesto.soltoNoAr)
    }

    const aoMover = (evento: PointerEvent) => {
      const gesto = gestoRef.current
      if (!gesto || gesto.pointerId !== evento.pointerId) return
      const deslocou = ponteiroArrastou(
        gesto.inicio,
        { x: evento.clientX, y: evento.clientY },
        evento.pointerType === 'mouse' ? TOLERANCIA_ARRASTO_PECA_MOUSE_PX : undefined,
      )
      if (!gesto.ativo) {
        if (!deslocou) return
        if (gesto.temporizador !== null) {
          // Touch movido antes da pressão prolongada: liberar para orbitar.
          limparGesto(true)
          return
        }
        gesto.ativo = true
        setArrastandoObjetoId(gesto.id)
      }
      if (!deslocou) return
      const objeto = objetos.find((item) => item.id === gesto.id)
      if (!objeto) return
      if (objeto.vitima?.local.tipo === 'veiculo') {
        if (!gesto.desvinculou) {
          gesto.desvinculou = true
          aoDesvincularVitima?.(gesto.id)
        }
        return
      }

      if (!canvas || !camera || !raycaster) return
      const rect = canvas.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return
      raycaster.setFromCamera(
        new THREE.Vector2(((evento.clientX - rect.left) / rect.width) * 2 - 1, -((evento.clientY - rect.top) / rect.height) * 2 + 1),
        camera,
      )

      let finalX: number
      let finalY: number
      let finalZ: number
      let novaRotacao = objeto.transformacao.rotacao

      if (podeApoiarEmVeiculo(objeto.tipo)) {
        const intersecao = buscarIntersecaoSuperficie(
          raycaster,
          veiculos3DRef.current,
          planoChao,
          objeto.tipo,
        )
        if (!intersecao) return
        const [x, z] = encaixeAtivo
          ? encaixarPosicaoXZ(intersecao.ponto.x, intersecao.ponto.z)
          : [intersecao.ponto.x, intersecao.ponto.z]
        finalX = x
        finalY = intersecao.ponto.y
        finalZ = z
        novaRotacao = calcularRotacaoSuperficie(
          objeto.tipo,
          intersecao.normal,
          objeto.transformacao.rotacao[1],
        )

        if (objeto.tipo === 'vitima') {
          const achado = ancoraMaisProxima({ x: finalX, z: finalZ }, objetos)
          const proximo = achado ? { idVeiculo: achado.idVeiculo, idAncora: achado.idAncora } : null
          ancoraDestacadaRef.current = proximo
          setAncoraDestacada((atual) =>
            atual?.idVeiculo === proximo?.idVeiculo && atual?.idAncora === proximo?.idAncora ? atual : proximo,
          )
        }
      } else {
        const ponto = raycaster.ray.intersectPlane(planoChao, new THREE.Vector3())
        if (!ponto) return
        // Após desvincular uma vítima, a posição atual já é calculada em mundo.
        if (!gesto.origem) gesto.origem = objeto.transformacao.posicao
        if (evento.shiftKey) {
          // Ajuste vertical direto: arrastar com Shift altera a elevação Y mantendo X e Z
          const deltaY = (gesto.inicio.y - evento.clientY) * (evento.altKey ? 0.0025 : 0.01)
          finalX = gesto.origem[0]
          finalZ = gesto.origem[2]
          finalY = Math.max(0, Number((gesto.origem[1] + deltaY).toFixed(2)))
          // erguido acima do terreno e solto: fica no ar e cai dali
          const chaoAbaixo = alturaDosObjetosEm(objetos, finalX, finalZ, objeto.id)
          gesto.soltoNoAr = objeto.tipo === 'trabalhador' && finalY > chaoAbaixo + DEGRAU_DE_QUEDA_M ? [finalX, finalY, finalZ] : null
        } else {
          // com Alt, a peça acompanha só uma fração do mouse: ajuste fino
          const fator = evento.altKey ? FATOR_AJUSTE_FINO : 1
          const x = gesto.origem[0] + (ponto.x - gesto.soloInicial.x) * fator
          const z = gesto.origem[2] + (ponto.z - gesto.soloInicial.z) * fator
          const [encX, encZ] = encaixeAtivo ? encaixarPosicaoXZ(x, z) : [x, z]
          finalX = encX
          // o objeto sobe e desce com o terreno que atravessa, mantendo a folga que já tinha
          finalY = ehTerreno(objeto.tipo)
            ? objeto.transformacao.posicao[1]
            : objeto.transformacao.posicao[1] +
              alturaDosObjetosEm(objetos, encX, encZ, objeto.id) -
              alturaDosObjetosEm(objetos, objeto.transformacao.posicao[0], objeto.transformacao.posicao[2], objeto.id)
          finalZ = encZ
        }
      }

      if (objeto.tipo === 'trabalhador' && !evento.shiftKey) {
        const [x0, y0, z0] = objeto.transformacao.posicao
        const borda = finalY < y0 - DEGRAU_DE_QUEDA_M ? degrauNoTrecho(objetos, objeto.id, x0, z0, finalX, finalZ) : null
        if (borda) {
          // passou por um degrau: cai dali (logo depois da borda), da altura em que estava
          gesto.soltoNoAr = [borda[0], y0, borda[1]]
        } else if (gesto.soltoNoAr) {
          // ainda no vazio: acompanha o dedo na altura da queda; voltou ao alto: desfaz
          gesto.soltoNoAr = finalY >= gesto.soltoNoAr[1] - DEGRAU_DE_QUEDA_M
            ? null
            : [finalX, gesto.soltoNoAr[1], finalZ]
        }
      }
      // imã: mosquetão, polia e anel de fita perto de um ponto de encaixe grudam nele
      if (ehPecaDeEncaixe(objeto.tipo, objeto.parametros)) {
        const comPosicao = objetos.map((o) => (o.id === objeto.id ? { ...o, transformacao: { ...o.transformacao, posicao: [finalX, finalY, finalZ] as VetorTabletop } } : o))
        const imaPara = pontoDeEncaixeMaisPerto({ versao: 2, nome: '', objetos: comPosicao, cordas: cordas ?? [], ancoragens: ancoragens ?? [] }, objeto.id)
        if (imaPara) {
          finalX += imaPara.pontoMundo[0] - imaPara.proprioMundo[0]
          finalY += imaPara.pontoMundo[1] - imaPara.proprioMundo[1]
          finalZ += imaPara.pontoMundo[2] - imaPara.proprioMundo[2]
        }
      }
      gesto.moveu = true
      aoTransformar(gesto.id, {
        ...objeto.transformacao,
        posicao: [finalX, finalY, finalZ],
        rotacao: novaRotacao,
      })
    }
    const aoSoltar = (evento: PointerEvent) => {
      if (gestoRef.current?.pointerId === evento.pointerId) limparGesto(false)
    }
    const aoCancelar = (evento: PointerEvent) => {
      if (gestoRef.current?.pointerId === evento.pointerId) limparGesto(true)
    }
    const aoPerderFoco = () => limparGesto(true)
    window.addEventListener('pointermove', aoMover)
    window.addEventListener('pointerup', aoSoltar)
    window.addEventListener('pointercancel', aoCancelar)
    window.addEventListener('blur', aoPerderFoco)
    return () => {
      window.removeEventListener('pointermove', aoMover)
      window.removeEventListener('pointerup', aoSoltar)
      window.removeEventListener('pointercancel', aoCancelar)
      window.removeEventListener('blur', aoPerderFoco)
    }
  }, [objetos, cordas, ancoragens, camera, raycaster, canvas, planoChao, encaixeAtivo, aoTransformar,
    aoAlterarManipulacao, aoCancelarManipulacao, aoVincularVitima, aoDesvincularVitima, aoSoltarTrabalhadorNoAr])

  const cancelarAoDesmontarRef = useRef(aoCancelarManipulacao)
  cancelarAoDesmontarRef.current = aoCancelarManipulacao
  useEffect(() => () => {
    const gesto = gestoRef.current
    if (gesto?.temporizador !== null && gesto?.temporizador !== undefined) window.clearTimeout(gesto.temporizador)
    if (gesto?.manipulando) cancelarAoDesmontarRef.current?.()
  }, [])

  // Tocar no chão cria uma nova peça vinda da bandeja, sem confundir órbita com clique.
  const pressaoRef = useRef<PontoDeTela | null>(null)
  useEffect(() => {
    const registrarPressao = (evento: PointerEvent) => {
      pressaoRef.current = { x: evento.clientX, y: evento.clientY }
    }
    window.addEventListener('pointerdown', registrarPressao)
    return () => window.removeEventListener('pointerdown', registrarPressao)
  }, [])

  // O toque que começou num objeto não desseleciona ao terminar. Em Montar,
  // o objeto recém-selecionado é remontado dentro do TransformControls; para
  // o R3F o clique já não "é" dele, e cai neste plano do chão — sem esta
  // marca, tocar num objeto o selecionava e o desselecionava no mesmo gesto.
  const pressaoEmObjetoRef = useRef(false)
  const selecionarPorToque = (id: string | null) => {
    pressaoEmObjetoRef.current = true
    aoSelecionar(id)
  }

  const aoTocarChao = (evento: ThreeEvent<MouseEvent>) => {
    if (escolhendoAlvo) return
    // órbita de câmera termina sobre este mesmo plano; sem a checagem de
    // arrasto, toda vez que a turma girasse a câmera nasceria uma peça nova
    if (ponteiroArrastou(pressaoRef.current, { x: evento.nativeEvent.clientX, y: evento.nativeEvent.clientY })) return
    if (!tipoParaPosicionar) {
      if (idSelecionado && !pressaoEmObjetoRef.current) aoSelecionar(null)
      return
    }
    // se o toque atravessou a superfície de um terreno (não a parede dele), a peça nasce sobre ela, não no chão atrás
    const sobreTerreno = ehTerreno(tipoParaPosicionar)
      ? undefined
      : evento.intersections.find((i) => i.object.userData?.terreno && faceVoltadaParaCima(i))
    const ponto = sobreTerreno?.point ?? evento.point
    const [x, z] = encaixeAtivo ? encaixarPosicaoXZ(ponto.x, ponto.z) : [ponto.x, ponto.z]
    // patamar da torre: a peça nasce no piso dele (a altura vem do toque, porque a torre não é um chão de altura única)
    const altura = sobreTerreno?.object.userData?.plataforma ? sobreTerreno.point.y : sobreTerreno ? alturaDosObjetosEm(objetos, x, z) : undefined
    aoPosicionarNovaPeca?.(tipoParaPosicionar, x, z, altura)
  }


  // o eixo de cada mosquetão, no referencial dele: ele gira para a carga ficar no eixo maior
  const equipamentosNaCena = useMemo<EquipamentosNaCena>(() => {
    const cena: CenaTabletopDominio = { versao: 2, nome: '', objetos, cordas: cordas ?? [], ancoragens: ancoragens ?? [] }
    const eixos = new Map<string, VetorTabletop>()
    for (const o of objetos) {
      if (o.tipo !== 'mosquetao') continue
      const { eixo } = orientacaoDoMosquetao(cena, o)
      if (!eixo) continue
      const [px, py, pz] = o.transformacao.posicao
      eixos.set(o.id, mundoParaLocalDoObjeto([px + eixo[0], py + eixo[1], pz + eixo[2]], o.transformacao))
    }
    const tipos = new Map(objetos.map((o) => [o.id, o.tipo]))
    const fatorDaPeca = (id: string) => (destaqueEquipamentos && TIPOS_EM_DESTAQUE.has(tipos.get(id)!) ? FATOR_DESTAQUE : 1)
    // na puxada, a corda anda a 0,6 m/s e arrasta cada roldana por onde passa
    const giros = new Map<string, number>()
    if (animarPuxada) {
      for (const corda of cordas ?? []) {
        if (!corda.sentido) continue
        for (const g of giroDasRoldanasNaCorda(cena, corda)) {
          const omega = (VELOCIDADE_PUXADA_M_S / (RAIO_RODANA_POLIA_M * fatorDaPeca(g.idObjeto))) * g.sentido * (corda.sentido === 'paraFim' ? 1 : -1)
          giros.set(`${g.idObjeto}:${g.roldana}`, omega)
        }
      }
    }
    return {
      eixosDosMosquetoes: eixos,
      fatorDaPeca,
      giroDasRoldanas: giros,
      fatorDasConexoes: destaqueEquipamentos ? FATOR_DESTAQUE : 1,
      animarPuxada,
    }
  }, [objetos, cordas, ancoragens, destaqueEquipamentos, animarPuxada])
  return (
    <ContextoEquipamentos.Provider value={equipamentosNaCena}>
    <ContextoPosicionando.Provider value={Boolean(tipoParaPosicionar)}>
    <ContextoDesenhoCorda.Provider value={Boolean(desenhoCorda || desenhoAncoragem || escolhendoPontoDeCorda)}>
    <group>
      {/* Pista de asfalto com faixas viárias, acostamento e marcas de frenagem */}
      {/* canal e cisterna são escavados: o chão vira uma chapa de asfalto com buracos, para se ver por dentro */}
      {buracos.length > 0 ? <Solo3D buracos={buracos} /> : chao === 'mato' ? <SoloMato3D /> : exibirAsfalto && <PistaAsfalto3D />}

      {/* Plano invisível: recebe o toque de "colocar peça aqui" e o de
          desselecionar ao tocar fora de qualquer objeto. Maior que a grade
          visual para cobrir também uma câmera bem afastada. Mesmo truque de
          `CenaEstabilizacao` (opacidade 0 em vez de `visible=false`, que
          continuaria clicável mas sem o controle fino de profundidade). */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, -0.001, 0]}
        onPointerDown={() => {
          pressaoEmObjetoRef.current = false
        }}
        onPointerMove={(evento) => {
          if (!tipoParaPosicionar) {
            if (pontoHoverPosicionar) setPontoHoverPosicionar(null)
            return
          }
          const sobreTerreno = ehTerreno(tipoParaPosicionar)
            ? undefined
            : evento.intersections.find((i) => i.object.userData?.terreno && faceVoltadaParaCima(i))
          const ponto = sobreTerreno?.point ?? evento.point
          const [x, z] = encaixeAtivo ? encaixarPosicaoXZ(ponto.x, ponto.z) : [ponto.x, ponto.z]
          const altura = sobreTerreno?.object.userData?.plataforma
            ? sobreTerreno.point.y
            : sobreTerreno
              ? alturaDosObjetosEm(objetos, x, z)
              : 0
          setPontoHoverPosicionar([x, altura, z])
        }}
        onPointerLeave={() => {
          if (pontoHoverPosicionar) setPontoHoverPosicionar(null)
        }}
        onClick={aoTocarChao}
      >
        <planeGeometry args={[240, 240]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* Indicador visual / ghost de prévia ao posicionar nova peça */}
      {tipoParaPosicionar && pontoHoverPosicionar && (
        <group position={[pontoHoverPosicionar[0], pontoHoverPosicionar[1] + 0.02, pontoHoverPosicionar[2]]}>
          <mesh rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.3, 0.45, 32]} />
            <meshBasicMaterial color="#3b82f6" transparent opacity={0.7} depthWrite={false} side={2} />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]}>
            <circleGeometry args={[0.3, 32]} />
            <meshBasicMaterial color="#60a5fa" transparent opacity={0.25} depthWrite={false} side={2} />
          </mesh>
          <mesh position={[0, 0.4, 0]}>
            <cylinderGeometry args={[0.04, 0.04, 0.8, 16]} />
            <meshBasicMaterial color="#3b82f6" transparent opacity={0.4} depthWrite={false} />
          </mesh>
        </group>
      )}

      {/* B4: bancos livres acendem enquanto uma vítima do chão é arrastada;
          o mais próximo (dentro do raio de encaixe) fica mais forte/opaco. */}
      {ancorasLivresNaCena.map((marcador) => {
        const destacada =
          ancoraDestacada?.idVeiculo === marcador.idVeiculo && ancoraDestacada?.idAncora === marcador.idAncora
        return (
          <mesh
            key={`${marcador.idVeiculo}:${marcador.idAncora}`}
            position={[marcador.posicao[0], marcador.posicao[1] + 0.03, marcador.posicao[2]]}
            rotation={[-Math.PI / 2, 0, 0]}
          >
            <ringGeometry args={destacada ? [0.28, 0.4, 32] : [0.32, 0.38, 32]} />
            <meshBasicMaterial
              color={destacada ? '#22c55e' : '#38bdf8'}
              transparent
              opacity={destacada ? 0.95 : 0.5}
              depthTest={false}
            />
          </mesh>
        )
      })}

      {/* Sangramento da vítima no chão ou lataria: fica no nível do mundo na cota da vítima */}
      {objetos.map((objeto) => {
        const sangramento = atendimento?.sangramentos?.[objeto.id]
        if (!sangramento || objeto.oculto || idsVinculados.has(objeto.id)) return null
        const [x, y, z] = objeto.transformacao.posicao
        const ySangue = Math.max(0.012, y + 0.005)
        return <Sangramento3D key={`sangue-${objeto.id}`} intensidade={sangramento} posicao={[x, ySangue, z]} />
      })}

      {(() => {
        const objetoSel = objetos.find((o) => o.id === idSelecionado)
        // mosquetão, polia e alça de fita são puxados para perto do ponto, como um imã
        const manipulandoMosquetao = Boolean(objetoSel && ehPecaDeEncaixe(objetoSel.tipo, objetoSel.parametros)) || tipoParaPosicionar === 'mosquetao'
        // com uma peça de corda na mão, os pontos aparecem: tocar num deles prende a peça ali
        const posicionandoPecaDeCorda = Boolean(aoPosicionarNoPonto && tipoParaPosicionar && PECAS_QUE_VAO_NO_PONTO.has(tipoParaPosicionar))
        const deveMostrarPontos = mostrarPontosAncoragem || manipulandoMosquetao || posicionandoPecaDeCorda
        const pontoEncaixeCandidato =
          manipulandoMosquetao && objetoSel
            ? pontoDeEncaixeMaisPerto({ versao: 2, nome: '', objetos, cordas, ancoragens }, objetoSel.id)
            : null

        return (
          <>
            {pontoEncaixeCandidato && (
              <group position={pontoEncaixeCandidato.pontoMundo as [number, number, number]}>
                <mesh rotation={[-Math.PI / 2, 0, 0]}>
                  <ringGeometry args={[0.08, 0.16, 32]} />
                  <meshBasicMaterial color={pontoEncaixeCandidato.ehAlcaDeFita ? '#4ade80' : '#38bdf8'} depthTest={false} transparent opacity={0.9} side={THREE.DoubleSide} />
                </mesh>
                <mesh>
                  <sphereGeometry args={[0.045, 12, 8]} />
                  <meshBasicMaterial color="#ffffff" depthTest={false} transparent opacity={0.95} />
                </mesh>
                {pontoEncaixeCandidato.ehAlcaDeFita && (
                  <Html position={[0, 0.3, 0]} center style={{ pointerEvents: 'none' }}>
                    <span className="tt-dica-ima">Solte para unir os anéis</span>
                  </Html>
                )}
              </group>
            )}
            {objetos.map((objeto) => {
              if (objeto.oculto || idsVinculados.has(objeto.id)) return null
              return (
                <ObjetoEditavel
                  key={objeto.id}
                  objeto={objeto}
                  selecionado={objeto.id === idSelecionado}
                  ferramenta={ferramenta}
                  vitimasVinculadas={objeto.veiculo ? vitimasPorVeiculo.get(objeto.id) ?? [] : []}
                  idVitimaSelecionada={idSelecionado}
                  corEquipamento={objeto.equipamento ? estadoPorEquipamento.get(objeto.id) : undefined}
                  avaliacaoEstabilizacao={objeto.veiculo ? avaliacoesPorVeiculo.get(objeto.id) : undefined}
                  pedidoTesteBalanco={pedidoTesteBalanco?.idVeiculo === objeto.id ? pedidoTesteBalanco : null}
                  pincel={pincel}
                  atendimento={atendimento}
                  somenteLeitura={somenteLeitura || escolhendoAlvo}
                  aoSelecionar={selecionarPorToque}
                  aoTransformar={aoTransformar}
                  aoAlterarManipulacao={aoAlterarManipulacao}
                  aoIniciarArrastoObjeto={escolhendoAlvo || ferramenta !== 'translate' ? undefined : iniciarCandidatoArrasto}
                  aoIniciarArrastoParaFora={escolhendoAlvo || ferramenta !== 'translate' ? undefined : iniciarCandidatoArrasto}
                  aoSelecionarPeca={(idPeca) => aoSelecionarPeca?.(objeto.id, idPeca)}
                  cenarioTravado={cenarioTravado}
                  mostrarPontosAncoragem={deveMostrarPontos}
                  desenhoCorda={desenhoCorda}
                  desenhoAncoragem={desenhoAncoragem}
                  aoEscolherPontoCorda={aoEscolherPontoCorda}
                  aoTocarPontoAncoragem={somenteLeitura || escolhendoAlvo ? undefined : aoTocarPontoAncoragem}
                  aoPosicionarNoPonto={somenteLeitura || escolhendoAlvo ? undefined : aoPosicionarNoPonto}
                  idPontoEscolhido={pontoEscolhido?.idObjeto === objeto.id ? pontoEscolhido.idPonto : null}
                  arrastoNaSessao={arrastoNaSessao}
                  aoRegistrarVeiculo3D={objeto.veiculo ? registrarVeiculo3D : undefined}
                  tipoParaPosicionar={tipoParaPosicionar}
                  aoPosicionarNovaPeca={aoPosicionarNovaPeca}
                  encaixeAtivo={encaixeAtivo}
                  pressaoRef={pressaoRef}
                />
              )
            })}
          </>
        )
      })()}

      {(cordas?.length || desenhoCorda) && (
        <Cordas3D
          objetos={objetos}
          cordas={cordas ?? []}
          ancoragens={ancoragens}
          idSelecionada={idCordaSelecionada}
          aoSelecionar={somenteLeitura ? undefined : aoSelecionarCorda}
          desenho={desenhoCorda}
          mostrarSetas={mostrarSetasTracao}
        />
      )}

      {idCordaSelecionada && !somenteLeitura && !desenhoCorda && aoReposicionarPontoCorda && (
        <AlcasCorda3D
          objetos={objetos}
          cordas={cordas ?? []}
          ancoragens={ancoragens}
          idCorda={idCordaSelecionada}
          aoReposicionar={aoReposicionarPontoCorda}
          aoAlterarManipulacao={aoAlterarManipulacao}
        />
      )}

      {!!cordas?.length && <AncoragemBasica3D objetos={objetos} cordas={cordas} />}

      {(ancoragens?.length || desenhoAncoragem || cordas?.some((c) => c.funcao === 'fita')) && (
        <Ancoragens3D
          objetos={objetos}
          cordas={cordas}
          ancoragens={ancoragens ?? []}
          idSelecionada={idAncoragemSelecionada}
          aoSelecionar={somenteLeitura ? undefined : aoSelecionarAncoragem}
          desenho={desenhoAncoragem}
          mostrarSetas={mostrarSetasTracao}
          // montando (peça na mão, corda ou ancoragem em desenho) o rótulo sai da frente dos pontos;
          // com as ações do ponto central abertas, o ângulo e a carga vão na barra
          ocultarRotulos={Boolean(tipoParaPosicionar || desenhoCorda || desenhoAncoragem)}
          pontoComAcoes={pontoEscolhido ?? null}
        />
      )}

      {mostrarZonaLivreQueda && talabartes && talabartes.length > 0 && <ZonaLivreQueda3D talabartes={talabartes} />}

      {quedaAnimada && (
        <QuedaAnimada3D
          resultado={quedaAnimada.resultado}
          quadro={quedaAnimada.quadro}
          indice={quedaAnimada.indice}
          engateAosPesM={parametrosDoObjeto(objetos.find((o) => o.id === quedaAnimada.resultado.idTrabalhador) ?? { tipo: 'trabalhador' }).engateAosPesM ?? 1.45}
        />
      )}

      {simulacaoAncoragem && <Simulacao3D {...simulacaoAncoragem} />}
    </group>
    </ContextoDesenhoCorda.Provider>
    </ContextoPosicionando.Provider>
    </ContextoEquipamentos.Provider>
  )
}

/**
 * Física de suspensão de UM veículo, imperativa (sem re-render do React a
 * cada quadro): o estado vive numa ref e o quadro só escreve direto na
 * transformação do grupo — com vários carros na cena isso evita recalcular
 * a árvore React 60x por segundo. A rigidez vem da avaliação de
 * estabilização DESTE carro: sem nada calçado, ele balança; travado (ou sem
 * avaliação — objeto sem bloco `veiculo`), fica parado.
 */
function useSuspensaoDoVeiculo(avaliacao: AvaliacaoEstabilizacaoPorVeiculo | undefined, ativa: boolean) {
  const grupoSuspensaoRef = useRef<Group>(null)
  const estadoRef = useRef<EstadoSuspensao>(ESTADO_SUSPENSAO_INICIAL)
  const perturbacaoRef = useRef<ForcaPerturbacao>({})

  useFrame((_, deltaBruto) => {
    if (!ativa) return
    const dt = Math.min(deltaBruto, 0.05)

    estadoRef.current = atualizarSuspensao(
      estadoRef.current,
      dt,
      avaliacao?.avaliacao.suspensaoAnulada ?? false,
      avaliacao?.avaliacao.rolamentoTravado ?? false,
      perturbacaoRef.current,
      undefined,
      avaliacao?.avaliacao.rigidezProgressiva,
    )
    // amortece o impulso da perturbação, como no loop de simulação da aba de Estabilização
    perturbacaoRef.current = {
      forcaY: (perturbacaoRef.current.forcaY ?? 0) * 0.82,
      torqueRoll: (perturbacaoRef.current.torqueRoll ?? 0) * 0.82,
      torquePitch: (perturbacaoRef.current.torquePitch ?? 0) * 0.82,
      forcaZ: (perturbacaoRef.current.forcaZ ?? 0) * 0.82,
    }

    const grupo = grupoSuspensaoRef.current
    if (grupo) {
      grupo.position.set(0, estadoRef.current.deslocamentoY, estadoRef.current.deslocamentoZ)
      grupo.rotation.set(estadoRef.current.anguloPitch, 0, estadoRef.current.anguloRoll)
    }
  })

  const perturbar = (forca: ForcaPerturbacao) => {
    perturbacaoRef.current = forca
  }

  return { grupoSuspensaoRef, perturbar }
}

/**
 * Só existe para objetos com bloco `veiculo` — é por isso que `useFrame` (via
 * `useSuspensaoDoVeiculo`) roda apenas para veículos de verdade, e não para
 * cada árvore, cone ou fragmento de vidro da cena. `perturbarRef` devolve o
 * "testar balanço" para `ObjetoEditavel`, que é quem recebe o clique.
 */
function ConteudoVeiculoSuspenso({
  objeto,
  corEquipamento,
  selecionado,
  vitimasVinculadas,
  idVitimaSelecionada,
  avaliacaoEstabilizacao,
  suspensaoAtiva,
  perturbarRef,
  atendimento,
  somenteLeitura,
  aoSelecionar,
  aoSelecionarPeca,
  aoIniciarArrastoParaFora,
  modoPincel,
}: {
  objeto: ObjetoTabletop
  corEquipamento: EstadoColocacao | undefined
  selecionado: boolean
  vitimasVinculadas: readonly ObjetoTabletop[]
  idVitimaSelecionada: string | null
  avaliacaoEstabilizacao: AvaliacaoEstabilizacaoPorVeiculo | undefined
  suspensaoAtiva: boolean
  perturbarRef: MutableRefObject<((forca: ForcaPerturbacao) => void) | null>
  atendimento?: AtendimentoCena3D
  somenteLeitura?: boolean
  aoSelecionar: (id: string) => void
  aoSelecionarPeca?: (idPeca: string | null) => void
  aoIniciarArrastoParaFora?: (id: string, evento: PointerEvent) => void
  modoPincel?: boolean
}) {
  const { grupoSuspensaoRef, perturbar } = useSuspensaoDoVeiculo(avaliacaoEstabilizacao, suspensaoAtiva)

  useEffect(() => {
    perturbarRef.current = perturbar
    return () => {
      perturbarRef.current = null
    }
  }, [perturbar, perturbarRef])

  return (
    <group ref={grupoSuspensaoRef}>
      <ModeloTabletop
        objeto={objeto}
        corEquipamento={corEquipamento}
        selecionado={selecionado}
        atendimento={atendimento}
        aoSelecionarPeca={aoSelecionarPeca}
        modoPincel={modoPincel}
      />
      {vitimasVinculadas.map((vitima) => (
        <VitimaVinculada3D
          key={vitima.id}
          objeto={vitima}
          veiculo={objeto}
          selecionado={vitima.id === idVitimaSelecionada}
          atendimento={atendimento}
          somenteLeitura={somenteLeitura}
          aoSelecionar={aoSelecionar}
          aoIniciarArrastoParaFora={aoIniciarArrastoParaFora}
        />
      ))}
    </group>
  )
}

function ObjetoEditavel({
  objeto,
  selecionado,
  ferramenta,
  vitimasVinculadas,
  idVitimaSelecionada,
  corEquipamento,
  avaliacaoEstabilizacao,
  pedidoTesteBalanco,
  atendimento,
  somenteLeitura,
  pincel,
  aoSelecionar,
  aoTransformar,
  aoAlterarManipulacao,
  aoIniciarArrastoObjeto,
  aoIniciarArrastoParaFora,
  aoSelecionarPeca,
  cenarioTravado,
  mostrarPontosAncoragem,
  desenhoCorda,
  desenhoAncoragem,
  aoEscolherPontoCorda,
  aoTocarPontoAncoragem,
  aoPosicionarNoPonto,
  idPontoEscolhido,
  arrastoNaSessao,
  aoRegistrarVeiculo3D,
  tipoParaPosicionar,
  aoPosicionarNovaPeca,
  encaixeAtivo,
  pressaoRef,
}: {
  objeto: ObjetoTabletop
  selecionado: boolean
  ferramenta: FerramentaTabletop
  vitimasVinculadas: readonly ObjetoTabletop[]
  idVitimaSelecionada: string | null
  corEquipamento: EstadoColocacao | undefined
  avaliacaoEstabilizacao: AvaliacaoEstabilizacaoPorVeiculo | undefined
  pedidoTesteBalanco: PedidoTesteBalanco | null
  atendimento?: AtendimentoCena3D
  somenteLeitura?: boolean
  pincel?: PropsCenaTabletop['pincel']
  aoSelecionar: (id: string) => void
  aoTransformar: (id: string, transformacao: TransformacaoTabletop) => void
  aoAlterarManipulacao?: (ativa: boolean) => void
  aoIniciarArrastoObjeto?: (id: string, evento: PointerEvent) => void
  aoIniciarArrastoParaFora?: (id: string, evento: PointerEvent) => void
  aoSelecionarPeca?: (idPeca: string | null) => void
  cenarioTravado?: boolean
  mostrarPontosAncoragem?: boolean
  desenhoCorda?: PropsCenaTabletop['desenhoCorda']
  desenhoAncoragem?: PropsCenaTabletop['desenhoAncoragem']
  aoEscolherPontoCorda?: PropsCenaTabletop['aoEscolherPontoCorda']
  aoTocarPontoAncoragem?: PropsCenaTabletop['aoTocarPontoAncoragem']
  aoPosicionarNoPonto?: PropsCenaTabletop['aoPosicionarNoPonto']
  idPontoEscolhido?: string | null
  arrastoNaSessao?: boolean
  aoRegistrarVeiculo3D?: (id: string, grupo: THREE.Group | null) => void
  tipoParaPosicionar?: TipoObjetoTabletop | null
  aoPosicionarNovaPeca?: PropsCenaTabletop['aoPosicionarNovaPeca']
  encaixeAtivo?: boolean
  pressaoRef?: MutableRefObject<PontoDeTela | null>
}) {
  const grupo = useRef<Group>(null)
  useEffect(() => {
    if (objeto.veiculo && grupo.current) {
      aoRegistrarVeiculo3D?.(objeto.id, grupo.current)
      return () => aoRegistrarVeiculo3D?.(objeto.id, null)
    }
  }, [objeto.id, objeto.veiculo, aoRegistrarVeiculo3D])
  const posicionando = useContext(ContextoPosicionando)
  // a alça da fita solta mostra sempre a bolinha do ponto: é o convite para clipar o mosquetão ali
  const alcaDeFitaLivre = objeto.tipo === 'pontoAncoragem' && objeto.parametros?.alcaFita === 1
  const desenhandoCorda = useContext(ContextoDesenhoCorda)
  const t = objeto.transformacao
  const bloqueado = objeto.bloqueado === true || (cenarioTravado && !ehEquipamentoTabletop(objeto))
  // o nó é da ponta da corda: segue a peça em que ela se prende, não se arrasta solto
  const arrastavel = objeto.tipo !== 'noOito' && objeto.tipo !== 'noFiel' && !bloqueado && (!somenteLeitura || (arrastoNaSessao === true && objeto.tipo !== 'vitima'))
  const modoPincel = Boolean(pincel?.ativo && selecionado && objeto.veiculo && !bloqueado && !somenteLeitura)
  const [previaAmassado, setPreviaAmassado] = useState<{
    ponto: THREE.Vector3
    regiao: string
  } | null>(null)
  const inicioToquePincel = useRef<PontoDeTela | null>(null)
  const perfilPincel = objeto.veiculo ? perfilVeiculoPorId(objeto.veiculo.idPerfil) : null
  const pontoDaSuperficie = (evento: ThreeEvent<PointerEvent | MouseEvent>) => {
    const raiz = grupo.current
    if (!raiz) return null
    raiz.updateWorldMatrix(true, false)
    const local = raiz.worldToLocal(evento.point.clone())
    // O modelo de SUV recebe esta escala interna em ModeloTabletop.
    if (objeto.tipo === 'suv') local.set(local.x, local.y / 1.08, local.z / 1.08)
    return local
  }
  const orientarPrevia = (regiao: string): [number, number, number] =>
    regiao === 'teto' ? [-Math.PI / 2, 0, 0]
      : regiao === 'traseira' ? [0, Math.PI, 0]
        : regiao === 'lateralEsquerda' ? [0, Math.PI / 2, 0]
          : regiao === 'lateralDireita' ? [0, -Math.PI / 2, 0] : [0, 0, 0]

  // suspensão física: só veículos sobre rodas balançam — tombado/capotado/
  // sobre outro veículo já estão apoiados por uma superfície maior, sem o
  // que a suspensão de quatro rodas simula. `perturbarRef` é a ponte para
  // `<ConteudoVeiculoSuspenso>` (só montado para veículos, para não gastar
  // um `useFrame` por objeto não-veículo da cena — árvore, cone, vidro...).
  const suspensaoAtiva = objeto.veiculo?.posicaoFinal === 'sobreRodas'
  const perturbarRef = useRef<((forca: ForcaPerturbacao) => void) | null>(null)

  const selecionar = (evento: ThreeEvent<PointerEvent>) => {
    // com o espaço apertado o gesto é da câmera: não seleciona nem arrasta a peça
    if (estadoNavegacao.espaco) return
    evento.stopPropagation()
    aoSelecionar(objeto.id)
    // B3: pressionar já candidata a peça para o arraste no chão — soltar sem
    // mover não desloca nada (a posição recalculada cai no mesmo lugar), e
    // continua valendo como seleção simples
    if (arrastavel && ferramenta === 'translate') {
      aoIniciarArrastoObjeto?.(objeto.id, evento.nativeEvent)
    }
    // selecionar NÃO testa o balanço: tocar num carro para ver as
    // propriedades ou focar uma vítima não pode sacudir a cena. O teste
    // vem só de `pedidoTesteBalanco` (botão "Testar estabilidade").
  }

  // o empurrão é aplicado no referencial DESTE carro (via grupo de
  // suspensão), então funciona girado e deslocado igual
  const serialTeste = pedidoTesteBalanco?.serial
  useEffect(() => {
    if (serialTeste === undefined || !suspensaoAtiva) return
    perturbarRef.current?.(FORCA_TESTE_BALANCO)
  }, [serialTeste, suspensaoAtiva])
  const publicarTransformacao = () => {
    const atual = grupo.current
    if (!atual) return
    const escala: [number, number, number] = [
      Math.max(0.05, atual.scale.x),
      Math.max(0.05, atual.scale.y),
      Math.max(0.05, atual.scale.z),
    ]
    atual.scale.set(...escala)
    aoTransformar(objeto.id, {
      posicao: [atual.position.x, atual.position.y, atual.position.z],
      rotacao: [atual.rotation.x, atual.rotation.y, atual.rotation.z],
      escala,
    })
  }

  const triagemDeclarada = atendimento?.triagensDeclaradas[objeto.id]
  const corTriagem = objeto.vitima
    ? (atendimento?.modoExibicao === 'aluno'
        ? (triagemDeclarada ? CORES_TRIAGEM_3D[triagemDeclarada] : '#94a3b8')
        : CORES_TRIAGEM_3D[objeto.vitima.triagem])
    : undefined

  const conteudo = (
    <group
      ref={grupo}
      // Nome estável para a câmera conseguir enquadrar o objeto selecionado.
      name={`${PREFIXO_SELECIONAVEL}${objeto.id}`}
      position={[t.posicao[0], t.posicao[1], t.posicao[2]]}
      rotation={[t.rotacao[0], t.rotacao[1], t.rotacao[2], 'YXZ']}
      scale={[t.escala[0], t.escala[1], t.escala[2]]}
      onPointerDown={desenhandoCorda ? undefined : modoPincel ? (evento) => {
        evento.stopPropagation()
        inicioToquePincel.current = { x: evento.nativeEvent.clientX, y: evento.nativeEvent.clientY }
      } : selecionar}
      onPointerMove={modoPincel ? (evento) => {
        const ponto = pontoDaSuperficie(evento)
        const alvo = ponto && perfilPincel ? alvoAmassadoNoPonto(perfilPincel, ponto) : null
        if (!ponto || !alvo) {
          setPreviaAmassado(null)
          return
        }
        setPreviaAmassado((anterior) => anterior?.regiao === alvo.regiao && anterior.ponto.distanceTo(ponto) < 0.03
          ? anterior : { ponto, regiao: alvo.regiao })
      } : undefined}
      onPointerOut={modoPincel ? () => setPreviaAmassado(null) : undefined}
      // Com pincel ligado, clique/tap aplica uma única deformação; movimento
      // antes de soltar não cria amassado nem desloca o veículo.
      onClick={desenhandoCorda ? undefined : modoPincel ? (evento) => {
        evento.stopPropagation()
        const inicio = inicioToquePincel.current
        inicioToquePincel.current = null
        if (ponteiroArrastou(inicio, { x: evento.nativeEvent.clientX, y: evento.nativeEvent.clientY })) return
        const ponto = pontoDaSuperficie(evento)
        if (ponto && perfilPincel && alvoAmassadoNoPonto(perfilPincel, ponto)) {
          pincel?.aoAplicar(objeto.id, ponto)
        }
      } : posicionando && tipoParaPosicionar && podeApoiarEmVeiculo(tipoParaPosicionar) && objeto.veiculo ? (evento) => {
        evento.stopPropagation()
        if (ponteiroArrastou(pressaoRef?.current ?? null, { x: evento.nativeEvent.clientX, y: evento.nativeEvent.clientY })) return
        const p = evento.point
        const [x, z] = encaixeAtivo ? encaixarPosicaoXZ(p.x, p.z) : [p.x, p.z]
        const normal = evento.face
          ? evento.face.normal.clone().transformDirection(evento.object.matrixWorld).normalize()
          : new THREE.Vector3(0, 1, 0)
        const rot = calcularRotacaoSuperficie(tipoParaPosicionar, normal, 0)
        aoPosicionarNovaPeca?.(tipoParaPosicionar, x, z, p.y, rot)
      } : posicionando ? undefined : pararPropagacao}
    >
      {objeto.veiculo ? (
        <ConteudoVeiculoSuspenso
          objeto={objeto}
          corEquipamento={corEquipamento}
          selecionado={selecionado}
          vitimasVinculadas={vitimasVinculadas}
          idVitimaSelecionada={idVitimaSelecionada}
          avaliacaoEstabilizacao={avaliacaoEstabilizacao}
          suspensaoAtiva={suspensaoAtiva}
          perturbarRef={perturbarRef}
          atendimento={atendimento}
          somenteLeitura={somenteLeitura}
          aoSelecionar={aoSelecionar}
          aoSelecionarPeca={aoSelecionarPeca}
          aoIniciarArrastoParaFora={aoIniciarArrastoParaFora}
          modoPincel={modoPincel}
        />
      ) : (
        <ModeloComDestaque
          objeto={objeto}
          corEquipamento={corEquipamento}
          selecionado={selecionado}
          atendimento={atendimento}
          aoSelecionarPeca={aoSelecionarPeca}
        />
      )}
      {modoPincel && previaAmassado && (
        <mesh
          raycast={() => null}
          position={objeto.tipo === 'suv'
            ? [previaAmassado.ponto.x, previaAmassado.ponto.y * 1.08, previaAmassado.ponto.z * 1.08]
            : [previaAmassado.ponto.x, previaAmassado.ponto.y, previaAmassado.ponto.z]}
          rotation={orientarPrevia(previaAmassado.regiao)}
        >
          <ringGeometry args={[Math.max(0.12, (pincel?.larguraM ?? 0.65) / 2),
            Math.max(0.12, (pincel?.larguraM ?? 0.65) / 2) + 0.025, 40]} />
          <meshBasicMaterial color="#38bdf8" transparent opacity={0.9} depthTest={false} side={THREE.DoubleSide} />
        </mesh>
      )}
      {objeto.vitima && (
        <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.42, 0.5, 32]} />
          <meshBasicMaterial color={corTriagem} depthTest={false} transparent opacity={0.85} />
        </mesh>
      )}
      {PECA_PEQUENA.has(objeto.tipo) && !selecionado && !mostrarPontosAncoragem && !desenhandoCorda && !alcaDeFitaLivre && !posicionando && (
        // área de toque invisível: peça pequena é difícil de acertar com o mouse e com o dedo
        <mesh>
          <sphereGeometry args={[RAIO_AREA_DE_TOQUE_M, 12, 10]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      )}
      {(selecionado || mostrarPontosAncoragem || desenhandoCorda || alcaDeFitaLivre) && (
        <PontosAncoragem3D
          objeto={objeto}
          aoEscolherPonto={
            desenhandoCorda && aoEscolherPontoCorda
              ? (idPonto) => aoEscolherPontoCorda({ idObjeto: objeto.id, idPonto })
              : !desenhandoCorda && tipoParaPosicionar && aoPosicionarNoPonto && PECAS_QUE_VAO_NO_PONTO.has(tipoParaPosicionar)
                ? (idPonto) => aoPosicionarNoPonto({ idObjeto: objeto.id, idPonto })
                : !desenhandoCorda && (selecionado || mostrarPontosAncoragem || alcaDeFitaLivre) && aoTocarPontoAncoragem
                ? (idPonto) => aoTocarPontoAncoragem({ idObjeto: objeto.id, idPonto })
                : undefined
          }
          idsEscolhidos={[
            ...(idPontoEscolhido ? [{ idObjeto: objeto.id, idPonto: idPontoEscolhido }] : []),
            ...(desenhoCorda?.percurso ?? []),
            ...(desenhoAncoragem ? [...desenhoAncoragem.fixacoes, ...(desenhoAncoragem.centro ? [desenhoAncoragem.centro] : [])] : []),
          ]
            .filter((r) => r.idObjeto === objeto.id)
            .map((r) => r.idPonto)}
        />
      )}
      {selecionado && (
        <mesh raycast={modoPincel ? () => null : undefined} position={[0, 0.035, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.7, 0.78, 36]} />
          <meshBasicMaterial color="#38bdf8" depthTest={false} transparent opacity={0.9} />
        </mesh>
      )}
    </group>
  )

  // Translação direta pela peça; o gizmo continua disponível para girar/escalar.
  if (!selecionado || bloqueado || somenteLeitura || modoPincel || ferramenta === 'translate') return conteudo

  return (
    <TransformControls
      mode={ferramenta}
      space={ferramenta === 'rotate' ? 'local' : 'world'}
      size={0.82}
      onObjectChange={publicarTransformacao}
      onMouseDown={() => aoAlterarManipulacao?.(true)}
      onMouseUp={() => {
        publicarTransformacao()
        aoAlterarManipulacao?.(false)
      }}
    >
      {conteudo}
    </TransformControls>
  )
}

/**
 * Poça de sangue sob a vítima enquanto a hemorragia da ficha não é controlada
 * — é o que qualquer um vê ao chegar. Menor quando o controle é parcial; some
 * quando o problema é controlado (ver `problemasVisiveisAtivos`).
 */
function Sangramento3D({ intensidade, posicao }: { intensidade: 'ativo' | 'parcial'; posicao: readonly [number, number, number] }) {
  const raio = intensidade === 'ativo' ? 0.75 : 0.4
  return (
    <mesh position={[posicao[0], posicao[1], posicao[2]]} rotation={[-Math.PI / 2, 0, 0]}>
      <circleGeometry args={[raio, 28]} />
      <meshBasicMaterial color="#6b0f0f" transparent opacity={0.92} depthWrite={false} />
    </mesh>
  )
}

/**
 * Vítima vinculada a um assento do veículo: renderizada como filha do grupo
 * do carro (por isso não recebe `position`/`rotation` próprios — herda a
 * rolagem, a guinada e o deslocamento do carro automaticamente), sem gizmo
 * de transformação — mover uma vítima vinculada é trocar de assento pelo
 * painel, não arrastar.
 */
function VitimaVinculada3D({
  objeto,
  veiculo,
  selecionado,
  atendimento,
  somenteLeitura,
  aoSelecionar,
  aoIniciarArrastoParaFora,
}: {
  objeto: ObjetoTabletop
  veiculo: ObjetoTabletop
  selecionado: boolean
  atendimento?: AtendimentoCena3D
  somenteLeitura?: boolean
  aoSelecionar: (id: string) => void
  /** B4 (volta pro chão): pressionar a vítima já sentada candidata a desvincular — só desvincula de fato se o dedo se mover (ver `CenaTabletop`). */
  aoIniciarArrastoParaFora?: (id: string, evento: PointerEvent) => void
}) {
  const posicionando = useContext(ContextoPosicionando)
  const colocacao = colocacaoLocalDaVitimaNoVeiculo(objeto, veiculo)
  if (!colocacao) return null

  const proporcoes = objeto.vitima ? perfilVitimaPorId(objeto.vitima.idPerfil) : ADULTO_MASCULINO
  const triagemDeclarada = atendimento?.triagensDeclaradas[objeto.id]
  const corTriagem = objeto.vitima
    ? (atendimento?.modoExibicao === 'aluno'
        ? (triagemDeclarada ? CORES_TRIAGEM_3D[triagemDeclarada] : '#94a3b8')
        : CORES_TRIAGEM_3D[objeto.vitima.triagem])
    : '#94a3b8'

  const temColar = atendimento
    ? (atendimento.modoExibicao === 'instrutor'
        ? objeto.vitima?.colarCervical
        : (atendimento.colaresColocados[objeto.id] ?? false))
    : objeto.vitima?.colarCervical

  const alturaMarcador =
    proporcoes.alturaPelve + proporcoes.alturaTronco + proporcoes.alturaPescoco + proporcoes.raioCabeca * 2 + 0.12

  return (
    <group
      onClick={posicionando ? undefined : pararPropagacao}
      onPointerDown={(evento) => {
        evento.stopPropagation()
        aoSelecionar(objeto.id)
        if (!somenteLeitura) {
          aoIniciarArrastoParaFora?.(objeto.id, evento.nativeEvent)
        }
      }}
    >
      <VitimaProcedural
        proporcoes={proporcoes}
        colocacao={colocacao}
        colarCervical={temColar}
        regiaoHemorragia={resolverRegiaoHemorragiaVitima(objeto)}
      />
      {atendimento?.sangramentos?.[objeto.id] && (
        <Sangramento3D
          intensidade={atendimento.sangramentos[objeto.id]!}
          // no assoalho, à frente do banco: o sangramento escorre para os pés
          posicao={[colocacao.posicaoPelve.x, Math.max(0.05, colocacao.posicaoPelve.y - 0.42), colocacao.posicaoPelve.z]}
        />
      )}
      {/* Marcador flutuante acima da cabine para seleção e identificação direta da vítima */}
      <group position={[colocacao.posicaoPelve.x, colocacao.posicaoPelve.y + alturaMarcador, colocacao.posicaoPelve.z]}>
        <mesh
          onClick={(e) => {
            e.stopPropagation()
            aoSelecionar(objeto.id)
          }}
          onPointerDown={(e) => {
            e.stopPropagation()
            aoSelecionar(objeto.id)
          }}
        >
          <sphereGeometry args={[0.13, 16, 14]} />
          <meshBasicMaterial color={selecionado ? '#38bdf8' : corTriagem} depthTest={false} />
        </mesh>
        {selecionado && (
          <mesh rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.18, 0.23, 24]} />
            <meshBasicMaterial color="#38bdf8" depthTest={false} transparent opacity={0.9} />
          </mesh>
        )}
      </group>
    </group>
  )
}

/** Veículo (detalhado ou casca sólida, conforme seleção) com o overlay de vidros trincados por cima. */
function VeiculoComVidros({
  perfil,
  objeto,
  selecionado,
  aoSelecionarPeca,
  modoPincel,
}: {
  perfil: PerfilVeiculo
  objeto: ObjetoTabletop
  selecionado: boolean
  aoSelecionarPeca?: (idPeca: string | null) => void
  modoPincel?: boolean
}) {
  const deformacao = objeto.veiculo ? estadoVisualDeformacoesTabletop(objeto.id, objeto.veiculo) : null

  const temIntervencaoPeca = Boolean(
    objeto.veiculo && (
      (objeto.veiculo.cortes && objeto.veiculo.cortes.length > 0) ||
      Object.values(objeto.veiculo.estadosPecas).some((e) => e !== 'normal')
    ),
  )

  return (
    <>
      {objeto.veiculo && !modoPincel && (selecionado || temIntervencaoPeca) ? (
        <VeiculoDetalhadoTabletop
          perfil={perfil}
          estadosPecas={objeto.veiculo.estadosPecas}
          pecaSelecionada={selecionado ? objeto.veiculo.pecaSelecionada : null}
          aoSelecionarPeca={selecionado ? aoSelecionarPeca : undefined}
          deformacao={deformacao}
        />
      ) : (
        <VeiculoAlvoCompleto perfil={perfil} corCarroceria={objeto.cor} deformacao={deformacao} />
      )}
      {objeto.veiculo && <VidrosVeiculoOverlay perfil={perfil} vidros={objeto.veiculo.vidros} />}
    </>
  )
}

const IDS_REGIAO_VIDRO: readonly IdVidro[] = [
  'parabrisa',
  'portaDianteiraEsquerda',
  'portaDianteiraDireita',
  'portaTraseiraEsquerda',
  'portaTraseiraDireita',
  'vigiaTraseiro',
]

/**
 * Marca visualmente, sobre a chapa/vidro já existente da carroceria, quais
 * vidros deste carro não estão mais intactos — sem recortar nem substituir a
 * geometria da carroceria (compartilhada com outras abas). Um vidro intacto
 * não desenha nada extra: a casca sólida já dá conta dele.
 */
function VidrosVeiculoOverlay({
  perfil,
  vidros,
}: {
  perfil: PerfilVeiculo
  vidros: Readonly<Partial<Record<IdVidro, EstadoManejoVidro>>>
}) {
  const afetados = IDS_REGIAO_VIDRO.filter((id) => {
    const estado = vidros[id]
    return estado !== undefined && estado !== 'intacto'
  })
  if (afetados.length === 0) return null

  const regioes = regioesVidroDoPerfil(perfil)
  return (
    <>
      {afetados.map((id) => {
        const reg = regioes[id]
        return (
          <group key={id} position={[...reg.posicao]} rotation={[...reg.rotacao]}>
            {/* Lâmina de vidro esbranquiçada/leitosa trincada */}
            <mesh position={[0, 0, 0.002]}>
              <planeGeometry args={[reg.dimensoes[0] * 0.98, reg.dimensoes[1] * 0.98]} />
              <meshStandardMaterial
                color="#e0f2fe"
                roughness={0.35}
                metalness={0.15}
                transparent
                opacity={0.65}
                side={2}
                depthWrite={false}
              />
            </mesh>
            {/* Rede de fratura tipo teia de aranha (impacto e estilhaçamento) */}
            <mesh position={[0, 0, 0.005]}>
              <planeGeometry args={[reg.dimensoes[0] * 0.98, reg.dimensoes[1] * 0.98, 12, 10]} />
              <meshBasicMaterial color="#ffffff" wireframe transparent opacity={0.92} side={2} />
            </mesh>
            <mesh position={[0, 0, 0.006]}>
              <ringGeometry args={[0.02, 0.18, 16]} />
              <meshBasicMaterial color="#ffffff" wireframe transparent opacity={0.8} side={2} />
            </mesh>
          </group>
        )
      })}
    </>
  )
}

/** A polia com as roldanas girando na puxada animada (o giro vem do contexto da cena). */
function PoliaNaCena({ idObjeto, dupla }: { idObjeto: string; dupla: boolean }) {
  const { giroDasRoldanas } = useEquipamentosNaCena()
  const giros = [giroDasRoldanas.get(`${idObjeto}:0`) ?? 0, giroDasRoldanas.get(`${idObjeto}:1`) ?? 0]
  return dupla ? <PoliaDuplaTabletop giros={giros} /> : <PoliaTabletop giro={giros[0]} />
}

/** No modo de destaque, os equipamentos pequenos crescem em volta da origem deles. */
function ModeloComDestaque(props: Parameters<typeof ModeloTabletop>[0]) {
  const { fatorDaPeca } = useEquipamentosNaCena()
  const f = fatorDaPeca(props.objeto.id)
  return f === 1 ? <ModeloTabletop {...props} /> : <group scale={f}><ModeloTabletop {...props} /></group>
}

/** O mosquetão girado para o eixo da carga, que vem do contexto da cena. */
function MosquetaoNaCena({ idObjeto }: { idObjeto: string }) {
  const { eixosDosMosquetoes } = useEquipamentosNaCena()
  return <MosquetaoTabletop eixo={eixosDosMosquetoes.get(idObjeto) ?? null} />
}

function ModeloTabletop({
  objeto,
  corEquipamento,
  selecionado,
  atendimento,
  aoSelecionarPeca,
  modoPincel,
}: {
  objeto: ObjetoTabletop
  corEquipamento?: EstadoColocacao
  selecionado: boolean
  atendimento?: AtendimentoCena3D
  aoSelecionarPeca?: (idPeca: string | null) => void
  modoPincel?: boolean
}) {
  switch (objeto.tipo) {
    case 'carro': {
      const perfil = objeto.veiculo ? perfilVeiculoPorId(objeto.veiculo.idPerfil) : HATCH_COMPACTO
      return <VeiculoComVidros perfil={perfil} objeto={objeto} selecionado={selecionado} aoSelecionarPeca={aoSelecionarPeca} modoPincel={modoPincel} />
    }
    case 'suv': {
      const perfil = objeto.veiculo ? perfilVeiculoPorId(objeto.veiculo.idPerfil) : SEDAN_MEDIO
      return (
        <group scale={[1, 1.08, 1.08]}>
          <VeiculoComVidros perfil={perfil} objeto={objeto} selecionado={selecionado} aoSelecionarPeca={aoSelecionarPeca} modoPincel={modoPincel} />
        </group>
      )
    }
    case 'caminhao':
      return <CaminhaoTabletop cor={objeto.cor ?? '#e2e8f0'} />
    case 'resgate':
      return <ResgateTabletop cor={objeto.cor} />
    case 'caminhaoBombeiros':
      return <CaminhaoBombeirosTabletop />
    case 'bombeiro':
      return <BombeiroTabletop />
    case 'bombeiroMedico':
      return <BombeiroMedicoTabletop />
    case 'areaUsa':
      return <AreaUsaTabletop />
    case 'moto':
      return <MotoTabletop cor={objeto.cor ?? '#dc2626'} />
    case 'arvore':
      return <ArvoreTabletop />
    case 'poste':
      return <PosteTabletop />
    case 'defensa':
      return <DefensaTabletop />
    case 'cone':
      return <ConeTabletop />
    case 'bloco':
      return <BlocoCribbing3D cor={corEquipamento ? CORES_ESTADO_EQUIPAMENTO[corEquipamento] : '#d4a35d'} />
    case 'vitima': {
      const proporcoes = objeto.vitima ? perfilVitimaPorId(objeto.vitima.idPerfil) : ADULTO_MASCULINO
      const postura = (objeto.vitima?.local.tipo === 'solo' ? objeto.vitima.local.postura : 'sentada') as PosturaVitimaSolo

      let yPelve = 0.45
      let reclinacaoRad = 0
      let rolagemRad = 0
      let pose = POSE_SENTADA

      switch (postura) {
        case 'emPe':
          yPelve = 0.92
          reclinacaoRad = 0
          rolagemRad = 0
          pose = POSE_ALINHADA
          break
        case 'sentada':
          yPelve = 0.45
          reclinacaoRad = 0
          rolagemRad = 0
          pose = POSE_SENTADA_NO_CHAO
          break
        case 'decubitoDorsal':
          yPelve = 0.12
          reclinacaoRad = Math.PI / 2
          rolagemRad = 0
          pose = POSE_ALINHADA
          break
        case 'decubitoVentral':
          yPelve = 0.12
          reclinacaoRad = -Math.PI / 2
          rolagemRad = 0
          pose = POSE_ALINHADA
          break
        case 'decubitoLateral':
          yPelve = 0.14
          reclinacaoRad = Math.PI / 2
          rolagemRad = Math.PI / 2
          pose = POSE_ALINHADA
          break
      }

      const temColar = atendimento
        ? (atendimento.modoExibicao === 'instrutor'
            ? objeto.vitima?.colarCervical
            : (atendimento.colaresColocados[objeto.id] ?? false))
        : objeto.vitima?.colarCervical

      return (
        <VitimaProcedural
          proporcoes={proporcoes}
          colocacao={{
            posicaoPelve: { x: 0, y: yPelve, z: 0 },
            guinadaRad: 0,
            reclinacaoRad,
            rolagemRad,
          }}
          pose={pose}
          colarCervical={temColar}
          regiaoHemorragia={resolverRegiaoHemorragiaVitima(objeto)}
        />
      )
    }
    case 'vidro':
      return <VidroTabletop />
    case 'detritosVidro': {
      const aparencia = atendimento?.riscos?.[objeto.id]
      // varrido na sessão: sai da cena (o cenário salvo continua com ele)
      if (aparencia?.fragmentosRemovidos) return null
      return (
        <>
          <DetritosVidroPersistentes3D tipo="estilhacosTemperado" local="solo" posicaoBase={[0, 0, 0]} />
          {aparencia?.coberto && <CobertorTabletop />}
          <AreaDeToque largura={0.9} profundidade={0.9} />
        </>
      )
    }
    case 'derramamento':
      return <DerramamentoTabletop aparencia={atendimento?.riscos?.[objeto.id]} />
    case 'caboEletrico':
      return <CaboEletricoTabletop aparencia={atendimento?.riscos?.[objeto.id]} />
    case 'manchaSangue':
      return <ManchaSangueTabletop aparencia={atendimento?.riscos?.[objeto.id]} />
    case 'fogo':
      return <FogoTabletop aparencia={atendimento?.riscos?.[objeto.id]} />
    case 'extintorPQS':
      return <ExtintorPqsTabletop />
    case 'lonaFerramentas':
      return <LonaFerramentasTabletop />
    case 'mochilaTrauma':
      return <MochilaTraumaTabletop />
    case 'calco':
      return (
        <StepChock3D
          cor={corEquipamento ? CORES_ESTADO_EQUIPAMENTO[corEquipamento] : '#1c2024'}
          comCunhaAjuste={objeto.equipamento?.comCunhaAjuste}
        />
      )
    case 'cunha':
      return <CunhaResgate3D cor={corEquipamento ? CORES_ESTADO_EQUIPAMENTO[corEquipamento] : '#1c2024'} />
    case 'vstrut':
      return (
        <VStrutTabletop
          cor={corEquipamento ? CORES_ESTADO_EQUIPAMENTO[corEquipamento] : undefined}
          alcance={objeto.equipamento?.alcance}
        />
      )
    case 'fitaCatraca':
      return <FitaCatracaTabletop cor={corEquipamento ? CORES_ESTADO_EQUIPAMENTO[corEquipamento] : undefined} />
    case 'cobertor':
      return <CobertorTabletop />
    case 'lagrima':
      return <LagrimaTabletop />
    case 'prancha':
      return <PranchaTabletop />
    case 'muro':
      return <MuroTabletop />
    case 'escada': {
      const p = parametrosDoObjeto(objeto)
      return <EscadaTabletop anguloGraus={p.anguloGraus!} comprimentoM={p.comprimentoM!} flexaoM={objeto.parametros?.flexaoM ?? 0} />
    }
    case 'toco': {
      const p = parametrosDoObjeto(objeto)
      return <TocoTabletop alturaM={p.alturaM!} diametroM={p.diametroM!} />
    }
    case 'maca':
      return <MacaTabletop />
    case 'polia':
      return <PoliaNaCena idObjeto={objeto.id} dupla={false} />
    case 'mosquetao':
      return <MosquetaoNaCena idObjeto={objeto.id} />
    case 'pontoAncoragem':
      return parametrosDoObjeto(objeto).alcaFita === 1 ? <AlcaFitaTabletop /> : <PontoAncoragemTabletop />
    case 'descensor':
      return <DescensorTabletop autoblocante={parametrosDoObjeto(objeto).tipoDescensor === 1} />
    case 'protetorCorda':
      return <ProtetorCordaTabletop comprimentoM={parametrosDoObjeto(objeto).comprimentoM!} />
    case 'placaAncoragem':
      return <PlacaAncoragemTabletop />
    case 'envolvimento':
      return <EnvolvimentoTabletop objeto={objeto} />
    case 'bloqueador':
      return <BloqueadorTabletop />
    case 'noOito':
      return <NoOitoTabletop cor={objeto.cor} />
    case 'noFiel':
      return <NoFielTabletop cor={objeto.cor} />
    case 'poliaDupla':
      return <PoliaNaCena idObjeto={objeto.id} dupla />
    case 'sistemaVM': {
      const p = parametrosDoObjeto(objeto)
      const c = composicaoDoSistema(p.razao!)
      return (
        <>
          <SistemaVMTabletop razao={p.razao!} cursoM={p.cursoM!} fusivel={p.fusivel} />
          <Rotulo3D posicao={[p.cursoM! / 2, 0.3, 0]} texto={p.fusivel ? `${c.nome} · ${tipoDeFusivel(p.fusivel).nome}` : c.nome} variante="neutro" sempreVisivel fator={5} />
        </>
      )
    }
    case 'trabalhador':
      return <TrabalhadorTabletop engateAosPesM={parametrosDoObjeto(objeto).engateAosPesM!} sentado={parametrosDoObjeto(objeto).sentadoNaCadeirinha === 1} />
    case 'torre': {
      const p = parametrosDoObjeto(objeto)
      return <TorreTabletop alturaM={p.alturaM!} larguraM={p.larguraM!} espacoPatamarM={p.espacoPatamarM!} />
    }
    case 'talude':
    case 'barranco':
    case 'vala':
    case 'canal':
    case 'cisterna':
      return <TerrenoTabletop objeto={objeto} />
    case 'ponte': {
      const p = parametrosDoObjeto(objeto)
      return (
        <PonteTabletop
          vaoM={p.vaoM!}
          larguraM={p.larguraM!}
          espessuraM={p.espessuraM!}
          guardaCorpoM={p.guardaCorpoM!}
          pilarAlturaM={p.pilarAlturaM!}
        />
      )
    }
    case 'cavalo':
      return <CavaloTabletop postura={parametrosDoObjeto(objeto).postura} />
    case 'vaca':
      return <VacaTabletop postura={parametrosDoObjeto(objeto).postura} />
    case 'estaca':
      return <EstacaTabletop configuracao={parametrosDoObjeto(objeto).configuracao} />
  }
}

/**
 * Veículo selecionado: estrutura completa (gaiola, portas, SRS, cintos),
 * cada um com seu próprio contexto de peças — assim vários carros detalhados
 * na mesma cena não compartilham estado de peça entre si.
 */
function VeiculoDetalhadoTabletop({
  perfil,
  estadosPecas,
  pecaSelecionada,
  aoSelecionarPeca,
  deformacao,
}: {
  perfil: PerfilVeiculo
  estadosPecas: MapaEstados
  pecaSelecionada?: string | null
  aoSelecionarPeca?: (idPeca: string | null) => void
  deformacao?: EstadoDeformacaoVeiculo | null
}) {
  const colunasSeccionadas = useMemo(() => {
    const lista: string[] = []
    for (const [id, estado] of Object.entries(estadosPecas)) {
      if (id.startsWith('coluna') && (estado === 'removida' || estado === 'afastada')) {
        if (id.includes('colunaA') && !lista.includes('colunaA')) lista.push('colunaA')
        if (id.includes('colunaB') && !lista.includes('colunaB')) lista.push('colunaB')
        if (id.includes('colunaC') && !lista.includes('colunaC')) lista.push('colunaC')
      }
    }
    return lista
  }, [estadosPecas])

  const valor: ValorMaquete = useMemo(
    () => ({
      perfil,
      modo: 'estrutura',
      camadas: CAMADAS_ESTRUTURA_TABLETOP,
      opacidadeCarroceria: OPACIDADE_CARROCERIA_TABLETOP,
      estados: estadosPecas,
      selecionada: pecaSelecionada ?? null,
      selecionar: aoSelecionarPeca ?? NOOP_SELECIONAR,
      mostrarRotulos: false,
      expansaoAirbag: 0,
      srsAcionados: LISTA_VAZIA,
      retencaoEmEvidencia: false,
      idsEmAlerta: LISTA_VAZIA,
      colunasSeccionadas,
      semCintoAncoras: LISTA_VAZIA,
      // no Table top a aula é sobre posição e estabilização, não sobre aço
      pinturaPorAco: false,
    }),
    [perfil, estadosPecas, pecaSelecionada, aoSelecionarPeca, colunasSeccionadas],
  )

  return (
    <ProvedorMaquete valor={valor}>
      <VeiculoDidatico perfil={perfil} deformacao={deformacao} />
    </ProvedorMaquete>
  )
}

function Material({ cor }: { cor: string }) {
  return <meshStandardMaterial color={cor} roughness={0.62} metalness={0.12} />
}

function CaminhaoTabletop({ cor }: { cor: string }) {
  return (
    <group position={[0, 0.58, 0]}>
      <mesh position={[-1.55, 0.58, 0]}>
        <boxGeometry args={[1.65, 1.8, 2.05]} />
        <Material cor={cor} />
        <Edges color="#17202a" />
      </mesh>
      <mesh position={[0.95, 0.72, 0]}>
        <boxGeometry args={[3.3, 1.55, 2.12]} />
        <Material cor="#9aa6af" />
        <Edges color="#17202a" />
      </mesh>
      {[-1.55, 0.25, 1.65].flatMap((x) =>
        [-1, 1].map((z) => (
          <mesh key={`${x}-${z}`} position={[x, 0, z * 0.98]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.42, 0.42, 0.22, 20]} />
            <meshStandardMaterial color="#111827" />
          </mesh>
        )),
      )}
    </group>
  )
}

function MotoTabletop({ cor }: { cor: string }) {
  return (
    <group position={[0, 0.38, 0]}>
      {[-0.72, 0.72].map((x) => (
        <mesh key={x} position={[x, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.34, 0.07, 10, 22]} />
          <meshStandardMaterial color="#111827" roughness={0.9} />
        </mesh>
      ))}
      <mesh rotation={[0, 0, -0.12]}>
        <CaixaArredondada args={[1.15, 0.18, 0.24]} />
        <Material cor={cor} />
      </mesh>
      <mesh position={[0.08, 0.3, 0]} rotation={[0, 0, -0.35]}>
        <cylinderGeometry args={[0.17, 0.24, 0.72, 12]} />
        <Material cor={cor} />
      </mesh>
      <mesh position={[-0.2, 0.45, 0]}>
        <CaixaArredondada args={[0.62, 0.12, 0.34]} />
        <meshStandardMaterial color="#202733" />
      </mesh>
    </group>
  )
}

function ArvoreTabletop() {
  return (
    <group>
      <mesh position={[0, 1.55, 0]}>
        <cylinderGeometry args={[0.2, 0.34, 3.1, 10]} />
        <meshStandardMaterial color="#76513b" roughness={1} />
      </mesh>
      {[[0, 3.25, 0], [0.55, 2.75, 0.1], [-0.52, 2.7, -0.2]].map((p, i) => (
        <mesh key={i} position={p as [number, number, number]}>
          <icosahedronGeometry args={[0.92, 1]} />
          <meshStandardMaterial color={i === 0 ? '#315b35' : '#3f7042'} roughness={1} />
        </mesh>
      ))}
    </group>
  )
}

function DefensaTabletop() {
  return (
    <group>
      <mesh position={[0, 0.62, 0]}>
        <CaixaArredondada args={[4.2, 0.42, 0.14]} />
        <meshStandardMaterial color="#aeb8c0" metalness={0.7} roughness={0.35} />
      </mesh>
      {[-1.7, 0, 1.7].map((x) => (
        <mesh key={x} position={[x, 0.31, 0]}>
          <CaixaArredondada args={[0.12, 0.62, 0.12]} />
          <meshStandardMaterial color="#737d84" metalness={0.65} />
        </mesh>
      ))}
    </group>
  )
}

function ConeTabletop() {
  return (
    <group>
      <mesh position={[0, 0.04, 0]}>
        <CaixaArredondada args={[0.55, 0.08, 0.55]} />
        <meshStandardMaterial color="#24272a" />
      </mesh>
      <mesh position={[0, 0.42, 0]}>
        <coneGeometry args={[0.24, 0.76, 18]} />
        <meshStandardMaterial color="#f97316" roughness={0.7} />
      </mesh>
    </group>
  )
}

/**
 * Sem `transmission` de propósito: com ela o three redesenha a cena opaca inteira
 * num buffer extra por quadro enquanto o vidro estiver visível. Com `opacity` de
 * 0,62 a refração quase não se vê, e a mistura por transparência basta.
 */
function VidroTabletop() {
  return (
    <group position={[0, 0.48, 0]}>
      <mesh>
        <boxGeometry args={[1.25, 0.82, 0.025]} />
        <meshPhysicalMaterial
          color="#bae6fd"
          roughness={0.06}
          metalness={0.2}
          transparent
          opacity={0.62}
          depthWrite={false}
        />
        <Edges color="#38bdf8" />
      </mesh>
      <mesh position={[0, 0, 0.016]}>
        <planeGeometry args={[1.18, 0.75, 6, 4]} />
        <meshBasicMaterial color="#ffffff" wireframe transparent opacity={0.45} />
      </mesh>
    </group>
  )
}

function VStrutTabletop({
  cor,
  alcance = [0.55, 1.15, 0],
}: {
  cor?: string
  alcance?: readonly [number, number, number]
}) {
  return (
    <VStrut3D
      para={[alcance[0], alcance[1], alcance[2]]}
      cor={cor}
      escala={0.92}
    />
  )
}

function FitaCatracaTabletop({ cor = '#ea580c' }: { cor?: string }) {
  return (
    <group>
      {/* Mecanismo tensor da catraca de alta tonelagem */}
      <group position={[0, 0.12, 0]}>
        {/* Corpo do chassi de aço zincado */}
        <mesh position={[0, 0, 0]} castShadow>
          <boxGeometry args={[0.22, 0.14, 0.22]} />
          <meshStandardMaterial color="#64748b" metalness={0.7} roughness={0.3} />
          <Edges color="#334155" />
        </mesh>
        {/* Tambor central com rolo de fita recolhida */}
        <mesh position={[0, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.045, 0.045, 0.18, 16]} />
          <meshStandardMaterial color={cor} roughness={0.6} />
        </mesh>
        {/* Alavanca de acionamento ergonômica amarela */}
        <group position={[0.08, 0.04, 0]} rotation={[0, 0, -0.45]}>
          <mesh position={[0, 0.08, 0]}>
            <CaixaArredondada args={[0.025, 0.16, 0.16]} />
            <meshStandardMaterial color="#475569" metalness={0.8} roughness={0.3} />
          </mesh>
          <mesh position={[0, 0.17, 0]}>
            <CaixaArredondada args={[0.035, 0.06, 0.18]} />
            <meshStandardMaterial color="#facc15" roughness={0.4} />
          </mesh>
        </group>
      </group>

      {/* Cinta/fita de poliéster de 50mm de amarração monobloco */}
      {/* Tramo ascendente ancorando na coluna/chassi do carro superior */}
      <mesh position={[0, 0.65, 0]} castShadow>
        <boxGeometry args={[0.015, 0.9, 0.12]} />
        <meshStandardMaterial color={cor} roughness={0.6} />
        <Edges color="#c2410c" />
      </mesh>
      {/* Gancho de aço forjado no topo da cinta */}
      <mesh position={[0, 1.1, 0]}>
        <torusGeometry args={[0.045, 0.016, 8, 16, Math.PI * 1.4]} />
        <meshStandardMaterial color="#334155" metalness={0.85} roughness={0.25} />
      </mesh>
      {/* Tramo descendente ancorando no veículo inferior / chassi */}
      <mesh position={[0, 0.02, 0.3]} rotation={[Math.PI / 2, 0, 0]}>
        <boxGeometry args={[0.015, 0.6, 0.12]} />
        <meshStandardMaterial color={cor} roughness={0.6} />
        <Edges color="#c2410c" />
      </mesh>
    </group>
  )
}

function CobertorTabletop() {
  return (
    <mesh position={[0, 0.06, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[1.1, 0.75, 6, 6]} />
      <meshStandardMaterial color="#94a3b8" roughness={0.85} side={2} />
    </mesh>
  )
}

const CORES_ABSORVENTE: Record<MaterialAbsorvente, string> = {
  serragem: '#b8864a',
  areia: '#d8c38e',
  absorvente_industrial: '#e5e7eb',
}

/**
 * Área invisível que recebe o toque de objetos finos ou pulverizados
 * (fragmentos de vidro, cabo caído): sem ela, acertar a peça com o dedo
 * dependeria de tocar exatamente numa partícula de poucos milímetros.
 */
function AreaDeToque({
  largura,
  profundidade,
  centro = [0, 0, 0],
}: {
  largura: number
  profundidade: number
  centro?: readonly [number, number, number]
}) {
  return (
    <mesh position={[centro[0], 0.05, centro[2]]}>
      <CaixaArredondada args={[largura, 0.1, profundidade]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  )
}

/**
 * Faixa zebrada de isolamento no chão (amarelo e preto), desenhada em
 * segmentos de anel — aparece quando a equipe isola a área de um risco.
 */
function IsolamentoArea3D({ raio }: { raio: number }) {
  const segmentos = 24
  const passo = (Math.PI * 2) / segmentos
  return (
    <group position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      {Array.from({ length: segmentos }, (_, i) => (
        <mesh key={i}>
          <ringGeometry args={[raio - 0.07, raio, 4, 1, i * passo, passo]} />
          <meshBasicMaterial color={i % 2 === 0 ? '#facc15' : '#111827'} side={2} />
        </mesh>
      ))}
    </group>
  )
}

/**
 * Mancha de óleo/combustível: escura e brilhante; maior enquanto o
 * vazamento continua; com a camada de serragem/areia/absorvente por cima
 * quando a equipe aplica.
 */
function DerramamentoTabletop({ aparencia }: { aparencia?: AparenciaRisco }) {
  const raio = aparencia?.vazando === false ? 0.85 : 1.1
  return (
    <group>
      {/* Base escura do óleo/combustível no piso com brilho especular */}
      <mesh position={[0, 0.012, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[1, 0.75, 1]}>
        <circleGeometry args={[raio, 48]} />
        <meshStandardMaterial color="#1c1917" roughness={0.06} metalness={0.4} />
      </mesh>

      {/* Camada líquida âmbar translúcida característica da gasolina/combustível */}
      <mesh position={[0, 0.014, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[0.96, 0.72, 1]}>
        <circleGeometry args={[raio * 0.94, 40]} />
        <meshStandardMaterial
          color="#b45309"
          roughness={0.12}
          metalness={0.3}
          transparent
          opacity={0.65}
          depthWrite={false}
        />
      </mesh>

      {/* Película de arco-íris iridescente (shimmer de hidrocarboneto sobre asfalto) */}
      <mesh position={[0, 0.016, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[0.88, 0.66, 1]}>
        <ringGeometry args={[raio * 0.35, raio * 0.82, 36]} />
        <meshStandardMaterial
          color="#06b6d4"
          emissive="#0284c7"
          emissiveIntensity={0.35}
          roughness={0.08}
          metalness={0.7}
          transparent
          opacity={0.48}
          depthWrite={false}
        />
      </mesh>

      {/* Borda / menisco dourado nítido para delimitar a poça contra o chão escuro */}
      <mesh position={[0, 0.013, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[1, 0.75, 1]}>
        <ringGeometry args={[raio * 0.94, raio * 1.02, 48]} />
        <meshStandardMaterial
          color="#f59e0b"
          emissive="#d97706"
          emissiveIntensity={0.5}
          roughness={0.2}
          metalness={0.3}
          transparent
          opacity={0.85}
          depthWrite={false}
        />
      </mesh>

      {/* Camada de absorvente sobreposta quando aplicada pela guarnição */}
      {aparencia?.absorvente && (
        <mesh position={[0, 0.022, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[1, 0.75, 1]}>
          <circleGeometry args={[0.86, 32]} />
          <meshStandardMaterial color={CORES_ABSORVENTE[aparencia.absorvente]} roughness={1} />
        </mesh>
      )}

      {/* Indicador de risco inflamável ativo */}
      {aparencia?.vazando !== false && (
        <group position={[0, 0.28, 0]}>
          <mesh rotation={[0, 0, Math.PI / 4]}>
            <planeGeometry args={[0.2, 0.2]} />
            <meshBasicMaterial color="#ef4444" side={THREE.DoubleSide} />
          </mesh>
          <mesh position={[0, 0, 0.005]} rotation={[0, 0, Math.PI / 4]}>
            <planeGeometry args={[0.15, 0.15]} />
            <meshBasicMaterial color="#fbbf24" side={THREE.DoubleSide} />
          </mesh>
        </group>
      )}

      {aparencia?.areaIsolada && <IsolamentoArea3D raio={1.7} />}
      <AreaDeToque largura={raio * 2.2} profundidade={raio * 1.6} />
    </group>
  )
}

/**
 * Cabo caído no chão, com uma faísca na ponta enquanto a rede estiver
 * energizada — só a confirmação de desligamento pela concessionária apaga.
 */
function CaboEletricoTabletop({ aparencia }: { aparencia?: AparenciaRisco }) {
  const energizado = aparencia?.energizado ?? true
  const curva = useMemo(
    () =>
      new THREE.CatmullRomCurve3([
        new THREE.Vector3(0, 0.03, 0),
        new THREE.Vector3(0.8, 0.03, 0.35),
        new THREE.Vector3(1.7, 0.03, -0.1),
        new THREE.Vector3(2.6, 0.03, 0.25),
      ]),
    [],
  )
  return (
    <group>
      <mesh>
        <tubeGeometry args={[curva, 32, 0.025, 8, false]} />
        <meshStandardMaterial color={energizado ? '#1f2937' : '#6b7280'} roughness={0.6} />
      </mesh>
      {/* Faíscas de choque elétrico na ponta energizada enquanto o circuito estiver ligado */}
      <FaiscasChoqueEletrico3D posicao={[2.6, 0, 0.25]} escala={1.15} ativo={energizado} />
      {aparencia?.areaIsolada && (
        <group position={[1.3, 0, 0.1]}>
          <IsolamentoArea3D raio={2.4} />
        </group>
      )}
      <AreaDeToque largura={2.9} profundidade={0.8} centro={[1.3, 0, 0.1]} />
    </group>
  )
}

/**
 * Foco de fogo / princípio de incêndio na cena tática.
 * Pode ser posicionado no veículo (motor, capô, habitáculo) ou em qualquer ponto da via.
 */
function FogoTabletop({ aparencia }: { aparencia?: AparenciaRisco }) {
  const extinto = aparencia?.extinto ?? false
  return (
    <group>
      <Fogo3D ativo={!extinto} escala={1.25} />
      {aparencia?.areaIsolada && (
        <group position={[0, 0, 0]}>
          <IsolamentoArea3D raio={2.2} />
        </group>
      )}
      <AreaDeToque largura={1.4} profundidade={1.4} centro={[0, 0, 0]} />
    </group>
  )
}

function LagrimaTabletop() {
  return (
    <mesh position={[0, 0.4, 0]}>
      <boxGeometry args={[0.6, 0.72, 0.04]} />
      <meshStandardMaterial color="#e2e8f0" transparent opacity={0.7} roughness={0.2} />
      <Edges color="#94a3b8" />
    </mesh>
  )
}

function PranchaTabletop() {
  return (
    <mesh position={[0, 0.1, 0]}>
      <boxGeometry args={[0.45, 0.05, 1.85]} />
      <meshStandardMaterial color="#f97316" roughness={0.6} />
      <Edges color="#7c2d12" />
    </mesh>
  )
}

function MuroTabletop() {
  return (
    <group>
      {/* Sapata de base larga tipo New Jersey assentada no solo */}
      <mesh position={[0, 0.12, 0]}>
        <CaixaArredondada args={[3.8, 0.24, 0.54]} />
        <meshStandardMaterial color="#475569" roughness={0.95} />
      </mesh>
      {/* Bloco de concreto rígido transversal ao impacto (largura em X, espessura em Z) */}
      <mesh position={[0, 0.88, 0]}>
        <boxGeometry args={[3.6, 1.36, 0.36]} />
        <meshStandardMaterial color="#64748b" roughness={0.92} />
        <Edges color="#334155" />
      </mesh>
      {/* Topo chanfrado de proteção */}
      <mesh position={[0, 1.58, 0]}>
        <CaixaArredondada args={[3.68, 0.08, 0.4]} />
        <meshStandardMaterial color="#94a3b8" roughness={0.85} />
      </mesh>
      {/* Faixa reflexiva amarela de tráfego/alerta na face frontal de impacto (-Z voltada para o veículo) */}
      <mesh position={[0, 0.88, -0.182]}>
        <planeGeometry args={[3.4, 0.2]} />
        <meshStandardMaterial color="#facc15" roughness={0.4} />
      </mesh>
    </group>
  )
}

